import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useParams, useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ImagePlus, Send, Trash2, Pencil, ChevronDown, ChevronUp, X } from 'lucide-react';
import api from '../api';
import socket from '../socket';
import LinkPreview from '../components/LinkPreview';
import AdPlaceholder from '../components/AdPlaceholder';
import VerdictBar, { getVerdict, getPostVerdict } from '../components/VerdictBar';
import ShareButton from '../components/ShareButton';
import EvidenceItem from '../components/EvidenceItem';
import IncidencesList from '../components/IncidencesList';
import CategoryPill from '../components/CategoryPill';
import {
  QUALITY_LABEL, QUALITY_COLOR, QUALITY_BG,
  ORIGIN_TYPE_ICON, getDomain,
} from '../utils/sourceLabels';
import { buildReplyGroups } from '../utils/replyGroups';

// Returns argument_classification falling back to 'argument' for backward compat
const COALESCE_ARG_CLASS = (node) => node?.argument_classification || 'argument';

// Returns true if two text strings are near-identical (substring or >80% length ratio)
function isSimilar(a, b) {
  if (!a || !b) return false;
  const norm = s => s.toLowerCase().trim();
  const na = norm(a), nb = norm(b);
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  const longer = Math.max(na.length, nb.length);
  if (longer === 0) return true;
  const shorter = Math.min(na.length, nb.length);
  return shorter / longer > 0.8;
}

// FIX 1: Build a nested tree from a flat array of arguments using parent_argument_id.
// Returns root nodes (those with no parent, or whose parent is not in the set) as an array.
function buildTree(flatArgs) {
  const byId = {};
  for (const a of flatArgs) byId[a.id] = { ...a, children: [] };
  const roots = [];
  for (const a of flatArgs) {
    // Incidents are shown in their own "incidents" list on the card they
    // support (IncidentSummaryCard). Leaving them in the reply tree too made
    // every incident appear twice (Move 5). Their own replies stay with them.
    if (a.argument_classification === 'incident') continue;
    const parent = a.parent_argument_id;
    if (parent && byId[parent]) {
      byId[parent].children.push(byId[a.id]);
    } else {
      roots.push(byId[a.id]);
    }
  }
  return roots;
}

const _regionNames = (() => { try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; } })();

const FlagIcon = ({ countryCode }) => {
  if (!countryCode) return null;
  const code = countryCode.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/16x12/${code}.png`}
      alt={countryCode}
      title={getCountryTitle(countryCode)}
      style={{
        width: 16,
        height: 12,
        marginLeft: 4,
        verticalAlign: 'middle',
        borderRadius: 2,
      }}
    />
  );
};

const getCountryTitle = (countryCode) => {
  if (!countryCode) return '';
  try { return _regionNames?.of(countryCode.toUpperCase()) || countryCode.toUpperCase(); } catch { return countryCode.toUpperCase(); }
};

function isLinkOnly(text) {
  // Only block truly empty submissions (no text, no URLs, no meaningful content)
  // Links are valid evidence — only block if there's nothing at all
  const withoutUrls = text.replace(/https?:\/\/[^\s]+/g, '').trim();
  const hasUrl = /https?:\/\//.test(text);
  const words = withoutUrls.split(/\s+/).filter(w => w.length > 0);
  // Block only if: no URLs AND no meaningful non-URL text
  return !hasUrl && words.length === 0;
}


// Deterministic avatar — consistent color per username
function Avatar({ username, size = 28 }) {
  const name = username || '?';
  const initials = name.split(/[\s_-]+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  let hash = 0;
  for (let i = 0; i < name.length; i++) { hash = name.charCodeAt(i) + ((hash << 5) - hash); }
  const bg = `hsl(${Math.abs(hash) % 360}, 50%, 38%)`;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, fontFamily: 'var(--font-body)', flexShrink: 0,
    }}>{initials}</div>
  );
}

// Source label helpers moved to ../utils/sourceLabels.js (shared with IncidentDetail).

// EvidenceItem moved to ../components/EvidenceItem.js (shared with IncidentDetail).

export default function PostDetail({ user }) {
  const { id, argId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost]               = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [highlightArgId, setHighlightArgId] = useState(null);
  const [breadcrumb, setBreadcrumb]   = useState([]);
  const [directReplies, setDirectReplies] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [analyzing, setAnalyzing]     = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState('');
  const [successToast, setSuccessToast] = useState(false);
  const [irrelevantArgs, setIrrelevantArgs] = useState([]);
  const [quarantineNotice, setQuarantineNotice] = useState(null); // { reason, reasoning }
  const [placementNotice, setPlacementNotice] = useState(null); // { type: 'single'|'multi', parentId?, synopsis?, locations? }
  const [fullArgTree, setFullArgTree] = useState(null); // FIX 1: nested tree from initial load

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const highlightedNodeRef = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const argCountRef         = useRef(0);
  const timeoutRef          = useRef(null);
  const pollingRef          = useRef(null);
  const currentNodeRef      = useRef(null);
  const pendingReplyRef     = useRef(false);
  const replyBoxRef         = useRef(null);
  const breadcrumbRef       = useRef([]);
  const directRepliesRef    = useRef([]);
  const submissionReplyIdsRef = useRef(null); // snapshot of all arg IDs at submission time
  const requestedParentArgIdRef = useRef(null); // node the user clicked reply on — for placement deviation detection
  const origNodeRef         = useRef(null);   // always points to the root original_node
  const navFromCodeRef      = useRef(false);  // suppress useEffect re-navigation when we navigate programmatically

  useEffect(() => { currentNodeRef.current = currentNode; }, [currentNode]);
  useEffect(() => { breadcrumbRef.current = breadcrumb; }, [breadcrumb]);
  useEffect(() => { directRepliesRef.current = directReplies; }, [directReplies]);

  // Scroll to a reply card and outline it for a few seconds. Cards render
  // after data arrives, so retry briefly until the card exists.
  const scrollToArg = useCallback((argId) => {
    setHighlightArgId(argId);
    setTimeout(() => setHighlightArgId(null), 4000);
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(`arg-${argId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else if (attempts < 15) setTimeout(() => tryScroll(attempts + 1), 200);
    };
    tryScroll();
  }, []);

  const handleUpdate = useCallback(async () => {
    try {
      const res = await api.get(`/posts/${id}`);
      const allArgs = res.data.threads.flatMap(t => t.arguments || []);
      const newCount = allArgs.length;
      // (D) fix: rebuild fullArgTree on every refresh so the merged root
      // renderer reflects landing replies — own submission, polling, OR
      // socket-triggered cross-tab. Pre-fix fullArgTree was set only in
      // initPage and stayed frozen, so handleUpdate's directReplies refresh
      // was invisible at root view where the tree drove rendering.
      setFullArgTree(buildTree(allArgs.filter(a => !a.is_original_post)));

      if (newCount > argCountRef.current) {
        argCountRef.current = newCount;

        const wasPending = pendingReplyRef.current;
        const snapshotIds = submissionReplyIdsRef.current;

        if (pendingReplyRef.current) {
          pendingReplyRef.current = false;
          replyBoxRef.current?.clear();
        }

        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        setAnalyzing(false);
        setTimeout(() => setSuccessToast(false), 3000);

        // Search ALL args for the newly submitted one
        if (wasPending && snapshotIds) {
          submissionReplyIdsRef.current = null;
          const allNewArgs = res.data.threads.flatMap(t => t.arguments || []);
          const newArgBasic = allNewArgs.find(a => !snapshotIds.has(a.id));

          // Placement deviation: if the AI placed the reply somewhere other than where the user clicked, surface a notice.
          const requestedParentId = requestedParentArgIdRef.current;
          requestedParentArgIdRef.current = null;
          if (requestedParentId && newArgBasic) {
            const newArgs = allNewArgs.filter(a => !snapshotIds.has(a.id));
            const origNode = res.data.original_node || origNodeRef.current;
            const truncate = s => (s && s.length > 80) ? s.slice(0, 77) + '...' : (s || '');
            const lookupSynopsis = (argId) => {
              if (argId === origNode?.id) {
                return truncate(origNode.synopsis || '') || 'the original post';
              }
              const a = allNewArgs.find(x => x.id === argId);
              return truncate(a?.synopsis) || `argument #${argId}`;
            };
            if (newArgs.length > 1) {
              setPlacementNotice({
                type: 'multi',
                locations: newArgs.map(a => ({
                  parentId: a.parent_argument_id,
                  synopsis: lookupSynopsis(a.parent_argument_id),
                })),
              });
              setTimeout(() => setPlacementNotice(null), 8000);
            } else {
              const a = newArgs[0];
              if (a.parent_argument_id && a.parent_argument_id !== requestedParentId) {
                setPlacementNotice({
                  type: 'single',
                  parentId: a.parent_argument_id,
                  synopsis: lookupSynopsis(a.parent_argument_id),
                });
                setTimeout(() => setPlacementNotice(null), 8000);
              }
            }
          }

          if (newArgBasic) {
            // Move 5: stay on the page the user is reading. Refresh what is on
            // screen, then scroll to and outline the new reply wherever it
            // landed. Before, the page jumped into the new reply's own view
            // (and the URL did not follow), which read as being kicked off the
            // post.
            const node = currentNodeRef.current;
            const origNode = res.data.original_node || origNodeRef.current;
            try {
              if (node?.is_original_post || !node?.id) {
                if (origNode) {
                  setCurrentNode(origNode);
                  const r = await api.get(`/arguments/${origNode.id}/replies`);
                  setDirectReplies(r.data.replies);
                }
              } else {
                const r = await api.get(`/arguments/${node.id}/replies`);
                setDirectReplies(r.data.replies);
              }
            } catch { /* keep what is on screen */ }
            scrollToArg(newArgBasic.id);
            return;
          }
        }

        const node = currentNodeRef.current;
        if (node?.is_original_post) {
          const origNode = res.data.original_node;
          if (origNode) {
            setCurrentNode(origNode);
            setBreadcrumb([]);
            const r = await api.get(`/arguments/${origNode.id}/replies`);
            setDirectReplies(r.data.replies);
          }
        } else if (node?.id) {
          const r = await api.get(`/arguments/${node.id}/replies`);
          setDirectReplies(r.data.replies);
        }
      } else {
        // Status-only update (re-eval finished) — refresh current node and its replies
        const node = currentNodeRef.current;
        if (node?.is_original_post) {
          const origNode = res.data.original_node;
          if (origNode) {
            setCurrentNode(origNode);
            if (origNode.id) {
              const r = await api.get(`/arguments/${origNode.id}/replies`);
              setDirectReplies(r.data.replies);
            }
          }
        } else if (node?.id) {
          const r = await api.get(`/arguments/${node.id}/replies`);
          setDirectReplies(r.data.replies);
        }
      }
    } catch (err) {
      console.error('[PostDetail] update refresh failed:', err);
    }
  }, [id, scrollToArg]);

  const debouncedRefresh = useRef(null);

  useEffect(() => {
    initPage();
    const postId = parseInt(id);

    const onDebateUpdated = ({ postId: updatedId }) => {
      if (updatedId !== postId) return;
      console.log('[SOCKET] debate_updated — scheduling refresh for post', id);
      console.log('[SUBMIT] Reply submitted, no page refresh should occur');
      if (debouncedRefresh.current) clearTimeout(debouncedRefresh.current);
      debouncedRefresh.current = setTimeout(() => {
        // Use handleUpdate instead of initPage to avoid showing loading state
        // (which unmounts ReplyBox and discards unsaved text)
        handleUpdate();
      }, 500);
    };

    // Re-fetch full post on feed_updated so has_prediction_claims, has_undefined_terms, etc.
    // are always in sync — partial patching missed these fields when analysis completed after load.
    const onFeedUpdated = async ({ postId: fPostId }) => {
      if (String(fPostId) !== String(postId)) return;
      console.log('[PostDetail] feed_updated: re-fetching post', fPostId);
      try {
        const res = await api.get(`/posts/${fPostId}`);
        if (res.data.post) setPost(res.data.post);
      } catch (err) {
        console.error('[PostDetail] feed_updated refetch failed:', err.message);
      }
    };

    socket.on('debate_updated', onDebateUpdated);
    socket.on('feed_updated',   onFeedUpdated);

    const onReconnect = () => {
      console.log('[SOCKET] reconnected — rejoining post:', postId);
      socket.emit('join_post', postId);
      handleUpdate();
    };
    socket.on('connect', onReconnect);
    socket.emit('join_post', postId);

    return () => {
      socket.emit('leave_post', postId);
      socket.off('debate_updated', onDebateUpdated);
      socket.off('feed_updated',   onFeedUpdated);
      socket.off('connect', onReconnect);
      if (debouncedRefresh.current) clearTimeout(debouncedRefresh.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };
  }, [id, handleUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount, fetch fresh post data so has_prediction_claims / has_undefined_terms
  // are present even if analysis completed before the socket handler was attached.
  useEffect(() => {
    if (id) {
      api.get(`/posts/${id}`).then(res => {
        if (res.data.post) {
          const p = res.data.post;
          setPost(p);
          console.log('[PostDetail] Mounted, fetched post:', id,
            'has_prediction_claims:', p.has_prediction_claims,
            'has_undefined_terms:', p.has_undefined_terms);
        }
      }).catch(() => {});
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // scrollToArg: if navigated from feed with location.state.scrollToArg, scroll and flash the target arg
  useEffect(() => {
    const targetId = location.state?.scrollToArg;
    if (!targetId) return;
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(`arg-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('arg-highlight-flash');
        setTimeout(() => el.classList.remove('arg-highlight-flash'), 1800);
      } else if (attempts < 10) {
        setTimeout(() => tryScroll(attempts + 1), 200);
      }
    };
    tryScroll();
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle URL-driven navigation (browser back/forward, direct arg links)
  useEffect(() => {
    if (navFromCodeRef.current) {
      navFromCodeRef.current = false;
      return;
    }
    if (!post) return;
    const rootNode = origNodeRef.current;
    if (!rootNode) return;

    if (!argId) {
      // Navigated back to root (e.g. browser back from /a/:argId URL)
      setCurrentNode(rootNode);
      setBreadcrumb([]);
      if (rootNode.id) loadReplies(rootNode.id);
      return;
    }

    // Navigated to a specific arg via URL (e.g. browser back/forward, direct link)
    (async () => {
      try {
        const bcRes = await api.get(`/arguments/${argId}/breadcrumb`);
        const chain = bcRes.data.breadcrumb;
        if (chain && chain.length > 0) {
          const targetNode = chain[chain.length - 1];
          const crumb = chain.slice(0, -1).map(c => c.is_original_post ? rootNode : c);
          setCurrentNode(targetNode);
          setBreadcrumb(crumb);
          setHighlightArgId(targetNode.id);
          setTimeout(() => setHighlightArgId(null), 2500);
          await loadReplies(targetNode.id);
        }
      } catch (err) {
        console.error('[PostDetail] breadcrumb fetch for argId failed:', err);
      }
    })();
  }, [argId, post]); // eslint-disable-line react-hooks/exhaustive-deps

  const initPage = async () => {
    navFromCodeRef.current = true; // initPage handles navigation — suppress useEffect re-navigation
    setLoading(true);
    try {
      const res = await api.get(`/posts/${id}`);
      setPost(res.data.post);

      api.get(`/posts/${id}/meta`).then(metaRes => {
        const m = metaRes.data;
        const setMeta = (id, content) => { const el = document.getElementById(id); if (el && content) el.setAttribute('content', content); };
        document.title = m.og_title || 'IsItTrueNow';
        setMeta('og-title', m.og_title);
        setMeta('og-description', m.og_description);
        setMeta('og-image', m.og_image || '');
        setMeta('tw-title', m.og_title);
        setMeta('tw-description', m.og_description);
        setMeta('tw-image', m.og_image || '');
      }).catch(() => {});

      let rootNode = res.data.original_node;
      if (!rootNode) {
        rootNode = {
          id: null, is_original_post: true,
          synopsis: res.data.post.content,
          full_content: res.data.post.content,
          stance: 'affirmative',
          status: res.data.post.post_status || 'neutral',
          status_reasoning: null,
          can_receive_replies: true,
          author_username: res.data.post.username,

        };
      }

      origNodeRef.current = rootNode;
      const allArgs = res.data.threads.flatMap(t => t.arguments || []);
      argCountRef.current = allArgs.length;
      // FIX 1: build full argument tree from all args for nested display
      setFullArgTree(buildTree(allArgs.filter(a => !a.is_original_post)));

      const targetArgId = argId || searchParams.get('argument');
      if (targetArgId) {
        const deepArg = allArgs.find(a => String(a.id) === String(targetArgId));
        if (deepArg) {
          setCurrentNode(deepArg);
          setBreadcrumb([rootNode]);
          setHighlightArgId(deepArg.id);
          setTimeout(() => setHighlightArgId(null), 2500);
          await loadReplies(deepArg.id);
          setLoading(false);
          return;
        }
        // Arg not in flat list — fetch breadcrumb chain to reconstruct deep tree position
        try {
          const bcRes = await api.get(`/arguments/${targetArgId}/breadcrumb`);
          const chain = bcRes.data.breadcrumb;
          if (chain && chain.length > 0) {
            const targetNode = chain[chain.length - 1];
            const crumb = chain.slice(0, -1).map(c => c.is_original_post ? rootNode : c);
            setCurrentNode(targetNode);
            setBreadcrumb(crumb);
            setHighlightArgId(targetNode.id);
            setTimeout(() => setHighlightArgId(null), 2500);
            await loadReplies(targetNode.id);
            setLoading(false);
            return;
          }
        } catch { /* fallthrough to root */ }
      }

      setCurrentNode(rootNode);
      setBreadcrumb([]);

      if (rootNode.id) {
        await loadReplies(rootNode.id);
      } else {
        setDirectReplies(allArgs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadReplies = async (nodeId) => {
    if (!nodeId) return;
    setRepliesLoading(true);
    try {
      const [repliesRes, irrelevantRes] = await Promise.all([
        api.get(`/arguments/${nodeId}/replies`),
        api.get(`/arguments/${nodeId}/irrelevant`).catch(() => ({ data: { irrelevant: [] } })),
      ]);
      const replies = repliesRes.data.replies || [];
      console.log(`[PostDetail] loadReplies(${nodeId}): received ${replies.length} replies`);
      replies.forEach(r => console.log(`  arg ${r.id} parent=${r.parent_argument_id} stance=${r.stance} status=${r.status}`));
      setDirectReplies(replies);
      setIrrelevantArgs(irrelevantRes.data.irrelevant || []);
    } catch (err) {
      console.error('[PostDetail] loadReplies failed:', err);
    } finally {
      setRepliesLoading(false);
    }
  };

  const navigateTo = useCallback(async (node, newBreadcrumb) => {
    navFromCodeRef.current = true;
    setCurrentNode(node);
    setBreadcrumb(newBreadcrumb);
    if (node.id) await loadReplies(node.id);
    if (node.is_original_post) {
      navigate(`/posts/${id}`);
    } else {
      navigate(`/posts/${id}/a/${node.id}`);
    }
  }, [id, navigate]);

  const navigateBreadcrumb = (index) => {
    const target = breadcrumb[index];
    navigateTo(target, breadcrumb.slice(0, index));
  };

  const clickViewThread = (replyNode) => {
    navigateTo(replyNode, [...breadcrumb, currentNode]);
  };

  // Replying on a card keeps you where you are (Move 5). The new reply is
  // scrolled to and outlined when it lands (see handleUpdate).
  const handleCardReplySubmit = (replyNode) => async (submitData) => {
    return handleReplySubmit({ ...submitData, parent_argument_id: replyNode.id });
  };

  const handleReplySubmit = async ({ content, image_url, image_public_id, image_ai_score, image_ai_flagged, parent_argument_id }) => {
    try {
      console.log('[UI] Submitting reply to:', id, 'endpoint: /api/posts/' + id + '/respond');
      console.log('Submitting reply with parent_argument_id:', parent_argument_id ?? null);
      // Capture the node the user clicked reply on — used after polling to detect placement deviation.
      requestedParentArgIdRef.current = parent_argument_id || currentNodeRef.current?.id || null;
      // Snapshot BEFORE posting so pipeline hasn't touched arg list yet
      try {
        const snapshotRes = await api.get(`/posts/${id}`);
        submissionReplyIdsRef.current = new Set(
          snapshotRes.data.threads.flatMap(t => t.arguments || []).map(a => a.id)
        );
      } catch { submissionReplyIdsRef.current = null; }

      const respondRes = await api.post(`/posts/${id}/respond`, {
        content,
        parent_argument_id: parent_argument_id || undefined,
        image_url: image_url || undefined,
        image_public_id: image_public_id || undefined,
        image_ai_score: image_ai_score ?? undefined,
        image_ai_flagged: image_ai_flagged ?? undefined,
      });

      // Quarantined (off-topic) — stored but excluded from debate
      if (respondRes.data?.quarantined) {
        setQuarantineNotice({ reason: respondRes.data.reason, reasoning: respondRes.data.reasoning });
        // Reload irrelevant section so the new entry appears
        const nodeId = currentNodeRef.current?.id;
        if (nodeId) {
          api.get(`/arguments/${nodeId}/irrelevant`)
            .then(r => setIrrelevantArgs(r.data.irrelevant || []))
            .catch(() => {});
        }
        return { quarantined: true, reasoning: respondRes.data.reasoning };
      }

      pendingReplyRef.current = true;
      setAnalyzing(true);
      setAnalysisMessage('');
      // Sprint A (Bug 2 fix): immediate accept-signal on api.post 201. The
      // pre-existing polling-based successToast only fires when newCount
      // increases — affirmative-merge replies never trigger it, so users
      // saw 15 seconds of silence and assumed failure. The toast now fires
      // on backend ACK; polling continues in the background to refresh the
      // tree when verdict re-eval completes.
      setSuccessToast(true);
      setTimeout(() => setSuccessToast(false), 3000);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(() => { handleUpdate(); }, 3000);

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        if (pendingReplyRef.current) {
          pendingReplyRef.current = false;
          submissionReplyIdsRef.current = null;
          requestedParentArgIdRef.current = null;
          setAnalyzing(false);
        }
      }, 15000);

      return true;
    } catch (err) {
      return err.response?.data?.message || err.response?.data?.error || 'Failed to submit response';
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Delete this post and all its threads? This cannot be undone.')) return;
    try {
      await api.delete(`/posts/${id}`);
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete post');
    }
  };

  if (loading) return <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 40, fontFamily: 'var(--font-body)' }}>Loading...</p>;
  if (!post)   return <p style={{ color: 'var(--color-invalidated)', fontFamily: 'var(--font-body)' }}>Post not found.</p>;

  const isRoot = currentNode?.is_original_post;

  const handleBack = () => {
    console.log('[NAV] handleBack called, breadcrumb length:', breadcrumb.length, 'post id:', id);
    if (breadcrumb.length > 0) {
      const prev = breadcrumb[breadcrumb.length - 1];
      if (prev.is_original_post) {
        navigate('/posts/' + id);
      } else {
        navigate('/posts/' + id + '/a/' + prev.id);
      }
    } else {
      navigate('/');
    }
  };

  // ── Grouped reply sections ────────────────────────────────────────────────
  // (B) fix: ONE merged renderer drives the root view (and non-root too) —
  // group top-level nodes by status, render each card with its own nested
  // descendant subtree below it. Removes the prior either/or conditional
  // that picked ReplyTree OR ReplyGroupList based on isRoot+fullArgTree.
  //
  // Source of truth:
  //   • At root, fullArgTree's top-level entries are direct replies to the OP,
  //     each with its descendants populated as `.children` by buildTree.
  //   • At non-root, directReplies is a flat list of the current node's
  //     direct children (no nested children populated — loadReplies returns
  //     only one level). Grouped at top, no nested expand-below — same as
  //     pre-fix non-root behavior.
  const topLevelReplies = isRoot ? fullArgTree : directReplies;
  // Sprint A Phase 2: grouping logic extracted to utils/replyGroups.js so
  // EvidencePage and IncidentDetail render the same buckets.
  const replyGroups = buildReplyGroups(topLevelReplies);

  return (
    <div className="post-detail-container">
      <button
        onClick={handleBack}
        className="back-btn"
        style={{
          background: 'transparent', border: 'none', padding: 0,
          color: '#64748b', fontSize: 13,
          display: 'inline-block', marginBottom: 16,
          fontFamily: 'var(--font-body)', cursor: 'pointer',
        }}
      >
        ← {breadcrumb.length > 0 ? 'Back to post' : 'Back to feed'}
      </button>

      {/* Breadcrumb trail — horizontal pill chain */}
      {breadcrumb.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4,
          marginBottom: 16,
        }}>
          {breadcrumb.map((node, i) => {
            const label = node.is_original_post
              ? (post?.post_meta?.og_title || node.synopsis || 'Original Post')
              : (node.synopsis || (node.author_username ? `Argument by ${node.author_username}` : 'Argument'));
            return (
              <React.Fragment key={node.id ?? `bc-${i}`}>
                {i > 0 && (
                  <span style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1, userSelect: 'none' }}>›</span>
                )}
                <button
                  onClick={() => navigateBreadcrumb(i)}
                  style={{
                    background: '#f1f5f9', border: 'none', borderRadius: 4,
                    padding: '3px 8px', fontSize: 12, color: '#64748b',
                    cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500,
                    maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                  title={label}
                >
                  {label}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Fix 2: "Replying to" parent context card */}
      {currentNode && !isRoot && breadcrumb.length > 0 && (() => {
        const parent = breadcrumb[breadcrumb.length - 1];
        if (!parent) return null;
        const parentLabel = parent.is_original_post
          ? (post?.post_meta?.og_title || parent.synopsis || 'Original Post')
          : (parent.synopsis || (parent.author_username ? `Argument by ${parent.author_username}` : 'Argument'));
        const truncated = parentLabel.length > 100 ? parentLabel.slice(0, 100) + '…' : parentLabel;
        return (
          <div
            onClick={() => navigateBreadcrumb(breadcrumb.length - 1)}
            style={{
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
              padding: '10px 14px', marginBottom: 8, cursor: 'pointer',
            }}
          >
            <p style={{ margin: '0 0 3px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-body)' }}>
              Replying to
            </p>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', fontStyle: 'italic', lineHeight: 1.4, fontFamily: 'var(--font-body)' }}>
              {truncated}
            </p>
          </div>
        );
      })()}

      {/* Highlighted node */}
      {currentNode && (
        <div ref={highlightedNodeRef}>
          <HighlightedNode
            node={currentNode}
            isRoot={isRoot}
            post={isRoot ? post : null}
            postId={post?.id}
            user={user}
            onDeletePost={isRoot ? handleDeletePost : null}
            onReplySubmit={handleReplySubmit}
          />
        </div>
      )}

      {/* User contributions panel — shown on root post when logged in */}
      {isRoot && user && (
        <UserContributionsPanel postId={post.id} onChange={handleUpdate} />
      )}

      {/* Always-visible reply box — normal for green/neutral, amber defense for red non-root */}
      {currentNode?.can_receive_replies && user && (
        <ReplyBox
          ref={replyBoxRef}
          user={user}
          analyzing={analyzing}
          analysisMessage={analysisMessage}
          onSubmit={handleReplySubmit}
        />
      )}
      {currentNode && !currentNode.can_receive_replies && !isRoot && currentNode.status === 'red' && user && (
        <ReplyBox
          ref={replyBoxRef}
          user={user}
          analyzing={analyzing}
          analysisMessage={analysisMessage}
          onSubmit={handleReplySubmit}
          defense
        />
      )}
      {analyzing && (
        <div style={{
          margin: '12px 0',
          background: 'var(--color-new-bg)',
          border: '1px solid var(--color-new-border)',
          borderRadius: 8, padding: '12px 16px', textAlign: 'center',
        }}>
          <p style={{ color: 'var(--color-new)', margin: 0, fontSize: 14, fontFamily: 'var(--font-body)' }}>Analyzing your argument...</p>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '4px 0 0', fontFamily: 'var(--font-body)' }}>The AI referee is reviewing logical structure.</p>
        </div>
      )}
      {/* Quarantine notice — shown when the user's post was off-topic */}
      {quarantineNotice && (
        <QuarantineNotice
          notice={quarantineNotice}
          onDismiss={() => setQuarantineNotice(null)}
        />
      )}
      {/* Success toast — fixed bottom-right, auto-dismisses */}
      {successToast && (
        <div style={{
          // On phones, sit above the 64px tab bar and stay inside the screen (Move 5).
          position: 'fixed', zIndex: 9999,
          ...(isMobile ? { bottom: 80, left: 16, right: 16, justifyContent: 'center' } : { bottom: 24, right: 24 }),
          background: '#1e293b', color: '#fff',
          borderRadius: 8, padding: '12px 20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          fontSize: 14, fontFamily: 'var(--font-body)',
          display: 'flex', alignItems: 'center', gap: 8,
          pointerEvents: 'none',
        }}>
          ✓ Argument added to the debate
        </div>
      )}
      {/* Placement deviation notice — appears when AI placed the reply at a different level than where the user clicked. Auto-dismisses after 8s. */}
      {placementNotice && (
        <div style={{
          position: 'fixed', zIndex: 9999,
          ...(isMobile ? { bottom: 136, left: 16, right: 16 } : { bottom: 80, right: 24, maxWidth: 420 }),
          background: '#1e293b', color: '#fff',
          borderRadius: 8, padding: '12px 16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          fontSize: 13, lineHeight: 1.45, fontFamily: 'var(--font-body)',
        }}>
          {placementNotice.type === 'single' ? (
            <>
              <div style={{ marginBottom: 6 }}>
                Your reply applies more broadly — it's been placed at: <span style={{ fontStyle: 'italic' }}>{placementNotice.synopsis}</span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => {
                    const target = placementNotice.parentId;
                    setPlacementNotice(null);
                    if (target === origNodeRef.current?.id) navigate(`/posts/${id}`);
                    else navigate(`/posts/${id}/a/${target}`);
                  }}
                  style={{
                    background: 'transparent', border: 'none', color: '#93c5fd',
                    cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 13,
                  }}
                >
                  View
                </button>
                <button
                  onClick={() => setPlacementNotice(null)}
                  style={{
                    background: 'transparent', border: 'none', color: '#94a3b8',
                    cursor: 'pointer', padding: 0, fontSize: 13,
                  }}
                >
                  Dismiss
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 6 }}>
                Your reply was placed in {placementNotice.locations.length} locations:
              </div>
              <ul style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>
                {placementNotice.locations.map((loc, i) => (
                  <li key={i} style={{ fontStyle: 'italic', marginBottom: 2 }}>{loc.synopsis}</li>
                ))}
              </ul>
              <button
                onClick={() => setPlacementNotice(null)}
                style={{
                  background: 'transparent', border: 'none', color: '#94a3b8',
                  cursor: 'pointer', padding: 0, fontSize: 13,
                }}
              >
                Dismiss
              </button>
            </>
          )}
        </div>
      )}
      {!user && (
        <p style={{ margin: '12px 0', fontSize: 13, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
          <Link to="/auth" style={{ color: 'var(--color-brand)', fontWeight: 600 }}>Sign in</Link> to reply.
        </p>
      )}

      {/* Direct replies */}
      <div style={{ marginTop: 24 }}>
        <h3 style={{
          fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)',
          marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em',
          fontFamily: 'var(--font-body)',
        }}>
          {isRoot ? 'Responses' : currentNode?.is_definition_thread ? 'Definition discussion' : 'Replies to this argument'}
        </h3>

        {repliesLoading ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, fontFamily: 'var(--font-body)' }}>Loading replies...</p>
        ) : topLevelReplies.length === 0 ? (
          <div style={{
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            borderRadius: 8, padding: 20, textAlign: 'center',
            color: 'var(--color-text-muted)', fontSize: 14,
            fontFamily: 'var(--font-body)',
          }}>
            No responses yet.
          </div>
        ) : currentNode?.is_definition_thread ? (
          // Definition threads show a flat chronological list — no argument
          // group labels. (Preserved special handling per spec; the
          // consolidation applies to argument-classified threads at root and
          // sub-arg views, not definition threads.)
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[...directReplies]
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
              .map(reply => (
                <ReplyCard
                  key={reply.id}
                  node={reply}
                  isOpinion={false}
                  user={user}
                  onViewThread={() => clickViewThread(reply)}
                  onSubmitReply={handleCardReplySubmit(reply)}
                  rawSubmitReply={handleReplySubmit}
                  sourcePostId={id}
                  highlightArgId={highlightArgId}
                />
              ))}
          </div>
        ) : (
          // (B) fix: ONE merged renderer. ReplyGroupList groups the top-level
          // entries by status and each ReplyGroup renders nested descendant
          // children below the card (see ReplyGroup body for the nesting
          // hook). At root, items are fullArgTree's roots (with .children).
          // At non-root, items are directReplies (flat — no .children, so
          // the nesting block no-ops and the visual matches pre-fix non-root.)
          <ReplyGroupList
            groups={replyGroups}
            postId={id}
            user={user}
            clickViewThread={clickViewThread}
            handleCardReplySubmit={handleCardReplySubmit}
            rawSubmitReply={handleReplySubmit}
            sourcePostId={id}
            highlightArgId={highlightArgId}
            isRoot={isRoot}
          />
        )}
        {irrelevantArgs.length > 0 && (
          <IrrelevantGroup args={irrelevantArgs} />
        )}
      </div>

      {/* Mobile floating scroll button */}
      {isMobile && breadcrumb.length > 0 && (
        <button
          onClick={() => highlightedNodeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          style={{
            position: 'fixed', bottom: 76, right: 16, zIndex: 900,
            width: 44, height: 44, borderRadius: '50%',
            background: 'var(--color-brand)', color: '#fff',
            border: 'none', fontSize: 20, cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title="Scroll to current argument"
        >
          ↓
        </button>
      )}
    </div>
  );
}

// ── Reply box ─────────────────────────────────────────────────────────────────
const ReplyBox = forwardRef(function ReplyBox({ user, analyzing, onSubmit, defense = false }, ref) {
  const [content, setContent]         = useState('');
  const [expanded, setExpanded]       = useState(false);
  const [imageFile, setImageFile]     = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [detectedUrl, setDetectedUrl] = useState(null);
  const [scrapedMeta, setScrapedMeta] = useState(null);
  const [showUrlPreview, setShowUrlPreview] = useState(true);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch og meta when URL is detected in reply text
  useEffect(() => {
    if (!detectedUrl) { setScrapedMeta(null); return; }
    api.get(`/posts/scrape-meta?url=${encodeURIComponent(detectedUrl)}`)
      .then(r => setScrapedMeta(r.data.meta || null))
      .catch(() => setScrapedMeta(null));
  }, [detectedUrl]);

  // Warn before unload when there is unsaved text
  useEffect(() => {
    if (!content && !imageFile) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [content, imageFile]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      setExpanded(true);
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    },
    clear: () => {
      setContent('');
      clearImage();
      setExpanded(false);
      setError('');
      setDetectedUrl(null);
      setScrapedMeta(null);
      setShowUrlPreview(true);
    },
  }));

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const charOver = content.length > 3000;
  const charNearLimit = content.length > 2800 && !charOver;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !imageFile) return;
    if (charOver) return;
    if (isLinkOnly(content)) {
      setShowUrlPreview(false);
      setError('Add some text to submit — your argument cannot be completely empty.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      let imageData = {};
      if (imageFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('image', imageFile);
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploading(false);
        imageData = {
          image_url:        uploadRes.data.url,
          image_public_id:  uploadRes.data.public_id,
          image_ai_score:   uploadRes.data.ai_detection?.ai_score ?? null,
          image_ai_flagged: uploadRes.data.ai_detection?.is_flagged ?? false,
        };
      }

      const result = await onSubmit({ content, ...imageData });
      if (result === true) {
        // Sprint A (Bug 1 fix): clear the composer immediately on success.
        // The parent's polling-based replyBoxRef.current.clear() only fires
        // when the reply creates a new arg node — affirmative-merge replies
        // (which update an existing node's contributions instead) never
        // trigger that path, leaving the composer full and the user
        // assuming the submit failed (drove Session 2's double-posting).
        setContent('');
        clearImage();
        setExpanded(false);
        setError('');
        setDetectedUrl(null);
        setScrapedMeta(null);
      } else if (result && typeof result === 'object' && (result.rejected || result.quarantined)) {
        // Rejection or quarantine — notice already shown via analysisMessage / quarantineNotice.
        // Preserve text in the box so the user can edit and resubmit.
        setUploading(false);
      } else {
        // Show 429/error inline without clearing input
        setError(typeof result === 'string' ? result : 'Failed to submit response');
        setUploading(false);
      }
    } catch (err) {
      setUploading(false);
      setError('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const hasText = content.trim().length > 0 || imageFile;

  return (
    <div style={{ margin: '16px 0' }}>
      {defense && (
        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309', fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Argument currently losing
          </span>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div style={{
          background: 'var(--color-surface)',
          border: defense ? '1px solid #f59e0b' : '1px solid var(--color-border)',
          borderRadius: 8,
          padding: '8px 12px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <textarea
            ref={textareaRef}
            rows={expanded ? 4 : 1}
            data-reply-box="true"
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--color-text-primary)', fontSize: 16, resize: 'none', width: '100%',
              lineHeight: 1.5, fontFamily: 'var(--font-body)',
              height: expanded ? 88 : 28, transition: 'height 0.15s ease',
            }}
            placeholder={defense ? 'This argument has been challenged — add evidence to defend it...' : 'Add your argument...'}
            value={content}
            onChange={e => {
              const val = e.target.value;
              setContent(val);
              if (val) setExpanded(true);
              const match = val.match(/https?:\/\/[^\s]+/);
              const url = match ? match[0] : null;
              if (url !== detectedUrl) {
                setDetectedUrl(url);
                setShowUrlPreview(true);
              }
            }}
            onFocus={() => setExpanded(true)}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if ((content.trim() || imageFile) && !submitting && !uploading && !charOver && !analyzing) {
                  handleSubmit(e);
                }
              }
            }}
          />

          {imagePreview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src={imagePreview} alt="preview"
                style={{ height: 48, width: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--color-border)' }}
              />
              <button type="button" onClick={clearImage}
                style={{
                  background: 'var(--color-border)', border: 'none', borderRadius: '50%',
                  color: 'var(--color-text-primary)', width: 18, height: 18, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <X size={10} />
              </button>
            </div>
          )}

          {detectedUrl && showUrlPreview && (
            <div style={{ position: 'relative' }}>
              <LinkPreview meta={scrapedMeta} url={detectedUrl} />
              <button
                type="button"
                onClick={() => setShowUrlPreview(false)}
                title="Dismiss preview"
                style={{
                  position: 'absolute', top: 6, right: 6,
                  background: 'var(--color-border)', border: 'none', borderRadius: '50%',
                  color: 'var(--color-text-primary)', width: 20, height: 20, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <X size={11} />
              </button>
            </div>
          )}

          <div className="reply-box-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={() => fileInputRef.current?.click()}
                title="Attach image"
                style={{
                  background: 'transparent', border: '1px solid var(--color-border)',
                  borderRadius: 5, color: 'var(--color-text-muted)', padding: '3px 8px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                }}>
                <ImagePlus size={14} />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
              {uploading && <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>Uploading...</span>}
              {content.length > 0 && (
                <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', color: charOver ? 'var(--color-invalidated)' : charNearLimit ? 'var(--color-disputed)' : 'var(--color-text-muted)' }}>
                  {content.length} / 3000
                </span>
              )}
            </div>

            {hasText && (
              <button type="submit" disabled={submitting || uploading || analyzing || charOver}
                className="submit-btn"
                style={{
                  padding: '5px 16px', background: 'var(--color-brand)', color: '#fff',
                  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  fontWeight: 600, fontFamily: 'var(--font-body)',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-brand-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-brand)')}
              >
                <Send size={12} />
                {submitting ? 'Submitting...' : defense ? 'Add defense' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </form>

      {expanded && !error && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0', fontFamily: 'var(--font-body)' }}>
          {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to submit
        </p>
      )}
      {error && <p style={{ color: 'var(--color-invalidated)', fontSize: 13, margin: '6px 0 0', fontFamily: 'var(--font-body)' }}>{error}</p>}
    </div>
  );
});

// ── Quarantine notice ─────────────────────────────────────────────────────────
function QuarantineNotice({ notice, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      margin: '12px 0',
      background: '#fef9c3',
      border: '1px solid #fde68a',
      borderRadius: 8, padding: '10px 14px',
      fontFamily: 'var(--font-body)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
            Your post was saved but marked as off-topic
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#78350f' }}>
            It won't affect the debate score. You can edit and resubmit with a more on-topic argument.
          </p>
          {notice.reasoning && (
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                background: 'none', border: 'none', padding: 0, marginTop: 6,
                color: '#b45309', fontSize: 12, cursor: 'pointer',
                fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {expanded ? 'Hide' : 'Why?'}
            </button>
          )}
          {expanded && notice.reasoning && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#78350f', fontStyle: 'italic', lineHeight: 1.5 }}>
              {notice.reasoning}
            </p>
          )}
        </div>
        <button
          onClick={onDismiss}
          style={{
            background: 'none', border: 'none', padding: 2, cursor: 'pointer',
            color: '#b45309', flexShrink: 0, display: 'flex', alignItems: 'center',
          }}
          title="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Irrelevant group ──────────────────────────────────────────────────────────
function IrrelevantGroup({ args }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 16 }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', padding: '4px 0',
          cursor: 'pointer', color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}
      >
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        Off-topic ({args.length})
      </button>
      {expanded && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {args.map(arg => (
            <div key={arg.id} style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 8, padding: '10px 14px',
              fontFamily: 'var(--font-body)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: '#92400e', background: '#fef9c3', border: '1px solid #fde68a',
                  padding: '1px 7px', borderRadius: 10,
                }}>
                  Not relevant
                </span>
                {arg.author_username && (
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>by {arg.author_username}</span>
                )}
              </div>
              {/* Sprint A (2.8 fix): show the user's actual reply text, not
                  the AI-generated synopsis. Synopses are inappropriate for
                  irrelevant content — they paraphrase a contribution that
                  was quarantined precisely because it isn't on-topic. The
                  status_reasoning still renders below as the quarantine
                  explanation. */}
              {(arg.full_content || arg.synopsis) && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {arg.full_content || arg.synopsis}
                </p>
              )}
              {arg.status_reasoning && (
                <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--color-text-muted)', fontStyle: 'italic', lineHeight: 1.4 }}>
                  {arg.status_reasoning}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Highlighted node ──────────────────────────────────────────────────────────
function HighlightedNode({ node, isRoot, post, postId, user, onDeletePost, onReplySubmit }) {
  const [geoOpen, setGeoOpen]       = useState(false);
  const [geoDist, setGeoDist]       = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [showMerged, setShowMerged] = useState(false);
  const geoRef = useRef(null);

  // Close the geo panel when clicking outside
  useEffect(() => {
    if (!geoOpen) return;
    const handler = (e) => { if (geoRef.current && !geoRef.current.contains(e.target)) setGeoOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [geoOpen]);

  const handleGeoClick = async () => {
    if (!geoOpen && geoDist === null) {
      setGeoLoading(true);
      try {
        const res = await api.get(`/posts/${postId}/geo-distribution`);
        setGeoDist(res.data.distribution || []);
      } catch { setGeoDist([]); }
      setGeoLoading(false);
    }
    setGeoOpen(o => !o);
  };

  const verdict = isRoot
    ? getPostVerdict(post || { root_status: node.status, is_new: false })
    : getVerdict(node.status, false);

  // PostDetail has no per-session isAnalyzing tracking for the original post.
  // Map verdict='new' → 'neutral' so the bar shows "Unverified" (gray) rather than
  // "Analyzing…" (blue) for posts whose analysis is either done or in progress.
  // The feed_updated socket listener will patch post.verdict in state as soon as
  // the pipeline finishes, updating this in real time.
  const displayVerdict = verdict === 'new' ? 'neutral' : verdict;

  const shareUrl = isRoot
    ? `${window.location.origin}/posts/${postId}`
    : `${window.location.origin}/posts/${postId}/a/${node.id}`;
  const shareTitle = (node.synopsis || node.full_content || '').slice(0, 80);

  const verdictBg = isRoot
    ? 'var(--color-surface)'
    : (node.status === 'red' ? 'var(--color-invalidated-bg)' : node.status === 'green' ? 'var(--color-verified-bg)' : 'var(--color-surface)');
  const cardBg = verdictBg;

  return (
    <div style={{
      background: cardBg,
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-card)',
      overflow: 'hidden',
      boxShadow: 'var(--shadow-card)',
    }}>
      <VerdictBar
        verdict={displayVerdict}
        counterArg={isRoot ? (() => {
          const raw = post?.strongest_counter_synopsis || post?.claim_statuses?.[0]?.strongest_rebuttal_synopsis || null;
          if (!raw) return null;
          if (raw.length <= 100) return raw;
          const cut = raw.slice(0, 100);
          const lastSpace = cut.lastIndexOf(' ');
          return (lastSpace > 60 ? cut.slice(0, lastSpace) : cut) + '…';
        })() : null}
        moreCount={post?.other_counter_count || 0}
        claimStatuses={isRoot ? post?.claim_statuses : null}
        predictionVerdict={isRoot ? post?.prediction_verdict : null}
      />
      {isRoot && post?.has_prediction_claims && (
        <div style={{
          padding: '5px 14px',
          background: '#f3e8ff',
          borderBottom: '1px solid #a855f7',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#6b21a8', fontFamily: 'var(--font-body)' }}>
            📊 Contains prediction claims
          </span>
          {post.prediction_verdict && (
            <span style={{
              fontSize: 11, fontWeight: 700, color: '#6b21a8',
              background: '#e9d5ff', border: '1px solid #a855f7',
              borderRadius: 10, padding: '1px 8px',
              fontFamily: 'var(--font-body)',
            }}>
              {post.prediction_verdict === 'plausible' ? 'Plausible' : post.prediction_verdict === 'implausible' ? 'Implausible' : 'Contested'}
            </span>
          )}
        </div>
      )}
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {isRoot ? (
              <span style={pill('var(--color-brand)', 'var(--color-brand-light)', 'var(--color-brand-light)')}>Original Post</span>
            ) : node.is_definition_thread ? (
              <>
                <span style={pill('#7c3aed', '#ede9fe', '#7c3aed')}>📖 Definition</span>
                {node.defines_term && (
                  <span style={{ fontSize: 11, color: '#6d28d9', fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                    Defining: {node.defines_term}
                  </span>
                )}
              </>
            ) : (
              <span style={pill(
                node.stance === 'affirmative' ? '#0d9488' : '#ffffff',
                node.stance === 'affirmative' ? '#ccfbf1' : '#1e293b',
                node.stance === 'affirmative' ? '#0d9488' : '#1e293b',
              )}>
                {node.stance === 'affirmative' ? 'Affirmative' : 'Rebuttal'}
              </span>
            )}
            {node.decisive && node.status === 'green' && (
              <span style={pill('var(--color-disputed)', 'var(--color-disputed-bg)', 'var(--color-disputed-border)')}>Key argument</span>
            )}
          </div>
          <div className="card-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isRoot && (
              <div ref={geoRef} style={{ position: 'relative' }}>
                <button
                  onClick={handleGeoClick}
                  title="Argument origins"
                  style={{
                    background: 'transparent', border: 'none', padding: 0,
                    cursor: 'pointer', fontSize: 16, lineHeight: 1,
                    display: 'flex', alignItems: 'center',
                    opacity: 0.65,
                  }}
                >🌍</button>
                {geoOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6,
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 10, padding: '12px 16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    minWidth: 220, maxWidth: 300, zIndex: 200,
                    fontFamily: 'var(--font-body)',
                  }}>
                    <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      Argument origins
                    </p>
                    {geoLoading ? (
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>Loading...</p>
                    ) : !geoDist || geoDist.length === 0 ? (
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>No data yet.</p>
                    ) : (() => {
                      const regionNames = (() => { try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; } })();
                      const shown = geoDist.slice(0, 5);
                      const rest = geoDist.slice(5);
                      const restCount = rest.reduce((s, r) => s + r.arg_count, 0);
                      return (
                        <>
                          {shown.map(row => (
                            <div key={row.country_code} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                              <FlagIcon countryCode={row.country_code} />
                              <span style={{ fontSize: 13, color: 'var(--color-text-primary)', flex: 1 }}>
                                {regionNames ? regionNames.of(row.country_code) : row.country_code}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>
                                {(row.affirmative_count || 0) > 0 && (
                                  <span style={{ color: 'var(--color-verified)' }}>{row.affirmative_count} for</span>
                                )}
                                {(row.affirmative_count || 0) > 0 && (row.rebuttal_count || 0) > 0 && (
                                  <span style={{ color: 'var(--color-text-muted)' }}>, </span>
                                )}
                                {(row.rebuttal_count || 0) > 0 && (
                                  <span style={{ color: 'var(--color-invalidated)' }}>{row.rebuttal_count} against</span>
                                )}
                                {!(row.affirmative_count || 0) && !(row.rebuttal_count || 0) && (
                                  <span style={{ color: 'var(--color-text-muted)' }}>{row.arg_count} arg{row.arg_count !== 1 ? 's' : ''}</span>
                                )}
                              </span>
                            </div>
                          ))}
                          {restCount > 0 && (
                            <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-text-muted)' }}>
                              + {restCount} from {rest.length} more {rest.length === 1 ? 'country' : 'countries'}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            <ShareButton url={shareUrl} title={shareTitle} />
            {onDeletePost && user && post && user.id === post.user_id && (
              <button onClick={onDeletePost}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'var(--color-invalidated)', cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 12, fontFamily: 'var(--font-body)',
                }}>
                <Trash2 size={13} />
                Delete post
              </button>
            )}
          </div>
        </div>

        {node.author_username && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Avatar username={node.author_username} size={24} />
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
              {node.author_username}
              <FlagIcon countryCode={isRoot ? post?.country_code : node.author_country_code} />
            </span>
          </div>
        )}

        {(() => {
          // Fix 7: avoid duplicate text for root posts
          // For link posts, prefer og_title as the heading; for regular posts use synopsis.
          // Only show full_content if it's meaningfully different from the heading AND not a bare URL.
          const headingText = isRoot
            ? (post?.post_meta?.og_title || node.synopsis)
            : node.synopsis;
          const isHeadingUrl = /^https?:\/\/\S+$/.test((headingText || '').trim());
          const bodyText = node.full_content;
          const bodyIsUrl = bodyText && /^https?:\/\/\S+$/.test(bodyText.trim());
          const bodyDiffersFromHeading = bodyText && bodyText !== headingText && bodyText !== node.synopsis;
          const showBody = bodyText && bodyDiffersFromHeading && !bodyIsUrl;
          // FIX 2: show original post.content beneath AI headline for root posts
          const originalContent = isRoot ? post?.content : null;
          const originalContentIsUrl = originalContent && /^https?:\/\/\S+$/.test(originalContent.trim());
          const showOriginalContent = isRoot && originalContent &&
            !originalContentIsUrl &&
            originalContent !== headingText &&
            originalContent !== node.synopsis;
          return (
            <>
              {headingText && !isHeadingUrl && (
                <p style={{
                  fontWeight: 600, fontSize: isRoot ? 17 : 14,
                  color: '#1e293b', marginBottom: (showOriginalContent || showBody) ? 4 : 10,
                  lineHeight: 1.4, fontFamily: isRoot ? 'var(--font-display)' : 'var(--font-body)',
                }}>
                  {headingText}
                </p>
              )}
              {isRoot && isHeadingUrl && node.synopsis && (
                <p style={{
                  fontWeight: 600, fontSize: 14, color: '#1e293b',
                  marginBottom: showOriginalContent ? 4 : 10, lineHeight: 1.4, fontFamily: 'var(--font-body)',
                }}>
                  {node.synopsis}
                </p>
              )}
              {showOriginalContent && (
                <p style={{
                  color: 'var(--color-text-secondary)', fontSize: 14,
                  lineHeight: 1.7, marginBottom: 10, fontFamily: 'var(--font-body)',
                }}>
                  {originalContent}
                </p>
              )}
              {!isRoot && showBody && (
                <p style={{
                  color: 'var(--color-text-secondary)', fontSize: 13,
                  lineHeight: 1.7, marginBottom: 10, fontFamily: 'var(--font-body)',
                }}>
                  {bodyText}
                </p>
              )}
            </>
          );
        })()}
        {!isRoot && node.status_reasoning && (
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', marginBottom: 12, fontFamily: 'var(--font-body)' }}>
            <strong style={{ fontStyle: 'normal', color: 'var(--color-text-secondary)' }}>Analysis:</strong> {node.status_reasoning}
          </p>
        )}
        {/* VC4: per-node verdict-level AI cumulative reasoning (when AI judged 3+ non-significant green rebuttals) */}
        {node.verdict_ai_reasoning && (
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', marginBottom: 12, fontFamily: 'var(--font-body)' }}>
            <strong style={{ fontStyle: 'normal', color: 'var(--color-text-secondary)' }}>AI judgment:</strong> {node.verdict_ai_reasoning}
          </p>
        )}

        {/* Evidence — detailed list for root posts. Only when the clickable
            "Supporting Evidence" list below has nothing; both read the same
            rows, so showing both listed every item twice (Move 5). */}
        {isRoot && node.evidence && node.evidence.length > 0 && !(node.evidence_checks?.length > 0) && (
          <div style={{ marginBottom: 14 }}>
            {node.evidence.map((ev, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <span style={{
                  color: ev.supports_claim ? 'var(--color-verified)' : 'var(--color-invalidated)',
                  fontSize: 13, flexShrink: 0,
                }}>
                  {ev.supports_claim ? '✓' : '✗'}
                </span>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', flex: 1, fontFamily: 'var(--font-body)' }}>
                  {ev.evidence_text}
                  {ev.evidence_url && (
                    <a href={ev.evidence_url} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--color-brand)', marginLeft: 6, fontSize: 12 }}>[source]</a>
                  )}
                </span>
                {ev.source_quality && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: QUALITY_COLOR[ev.source_quality] || 'var(--color-text-muted)',
                    background: QUALITY_BG[ev.source_quality] || 'var(--color-surface-hover)',
                    border: `1px solid ${QUALITY_COLOR[ev.source_quality] || 'var(--color-border)'}40`,
                    padding: '2px 7px', borderRadius: 10, flexShrink: 0,
                    fontFamily: 'var(--font-body)',
                  }}>
                    {QUALITY_LABEL[ev.source_quality] || ev.source_quality}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Source pills — for argument nodes (non-root highlighted) */}
        {!isRoot && node.evidence && node.evidence.length > 0 && (
          <div style={{ marginBottom: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-body)', marginRight: 2 }}>
              Sources:
            </span>
            {node.evidence.map((ev, i) => {
              const icon = ORIGIN_TYPE_ICON[ev.source_origin_type] || '📎';
              const isAssertion = ev.source_origin_type === 'assertion_only';
              return ev.evidence_url ? (
                <a key={i} href={ev.evidence_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: 'var(--color-brand)', background: 'var(--color-surface-hover)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '3px 8px', textDecoration: 'none', fontFamily: 'var(--font-body)' }}>
                  <span style={{ fontSize: 11 }}>{icon}</span>
                  {getDomain(ev.evidence_url)}
                </a>
              ) : isAssertion ? (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 3, fontSize: 12, color: '#b45309', fontFamily: 'var(--font-body)', lineHeight: 1.4 }}>
                  <span style={{ fontSize: 11, flexShrink: 0 }}>{icon}</span>
                  <span>Claimed: {ev.evidence_text || ''}</span>
                </span>
              ) : (
                <span key={i} title={ev.evidence_text} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: 'var(--color-text-muted)', background: 'var(--color-surface-hover)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '3px 8px', fontFamily: 'var(--font-body)' }}>
                  <span style={{ fontSize: 11 }}>{icon}</span>
                  {ev.evidence_text?.slice(0, 60) || 'Source'}
                </span>
              );
            })}
          </div>
        )}

        {/* Attached media (non-root) */}
        {!isRoot && node.media_urls && node.media_urls.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {node.media_urls.map((url, i) =>
              url && url.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                <img key={i} src={url} alt=""
                  style={{ width: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 8, marginBottom: 6, background: 'var(--color-bg)' }} />
              ) : (
                <LinkPreview key={i} meta={null} url={url} />
              )
            )}
          </div>
        )}

        {/* Post image (root only) */}
        {isRoot && post?.image_url && (
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <img src={post.image_url} alt=""
              style={{ width: '100%', maxHeight: 500, objectFit: 'contain', borderRadius: 8, display: 'block', background: 'var(--color-bg)' }} />
            {(() => {
              const score = post.image_ai_score;
              if (!score || score < 0.40) return null;
              const pct = Math.round(score * 100);
              const badge = score < 0.70
                ? { label: `${pct}% AI probability`,     color: '#92400e', bg: '#fef3c7' }
                : score < 0.90
                ? { label: `${pct}% likely AI-generated`, color: '#c2410c', bg: '#ffedd5' }
                : { label: `${pct}% AI-generated`,        color: '#ffffff', bg: '#dc2626' };
              return (
                <span style={{
                  position: 'absolute', top: 8, left: 8,
                  background: badge.bg, color: badge.color,
                  fontSize: 11, fontWeight: 700, padding: '3px 10px',
                  borderRadius: 12, fontFamily: 'var(--font-body)',
                }}>{badge.label}</span>
              );
            })()}
          </div>
        )}

        {isRoot && post?.source_url && <LinkPreview meta={post.post_meta} url={post.source_url} linkVerdict={post.link_verdict} />}
        {isRoot && (() => {
          console.log('[PostDetail] Claims:', post?.claims?.length, 'has_undefined_terms:', post?.has_undefined_terms);
          return null;
        })()}
        {/* Supporting arguments merged into root post */}
        {isRoot && (() => {
          const rootMerges = node?.merge_members?.filter(m => String(m.merged_into) === String(node.id)) || [];
          if (rootMerges.length === 0) return null;
          return (
            <div style={{ marginBottom: 14 }}>
              <button
                onClick={() => setShowMerged(s => !s)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: 'var(--color-text-muted)', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                {showMerged ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {showMerged ? 'Hide' : 'Show'}{' '}{rootMerges.length}{' '}supporting argument{rootMerges.length === 1 ? '' : 's'}
              </button>
              {showMerged && rootMerges.map(m => (
                <div key={m.id} style={{
                  padding: '8px 12px',
                  borderLeft: '3px solid #86efac',
                  marginTop: 8,
                  fontSize: 13,
                  color: '#374151',
                  fontFamily: 'var(--font-body)',
                }}>
                  {m.full_content}
                </div>
              ))}
            </div>
          );
        })()}
        {isRoot && post && post.claims && post.claims.length > 0 && (
          <ClaimsPanel claims={post.claims} claimStatuses={post.claim_statuses} />
        )}
        {/* Sprint A Fix A: merged contributions visible on the OP/current-node
            card. Same rendering pattern as ReplyCard's "Also raised" block —
            non-originator contributions are surfaced as raw user text +
            author. Without this, affirmative-to-root merges (responses
            50/52/51b in the June-2 post-47 trace) were silently swallowed. */}
        {(() => {
          const merged = (node?.contributions || []).filter(c => !c.is_originator && c.contribution_text);
          if (merged.length === 0) return null;
          return (
            <div style={{
              marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 12,
            }}>
              <div style={{
                fontSize: 11, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                marginBottom: 8, fontFamily: 'var(--font-body)',
              }}>
                Also raised ({merged.length})
              </div>
              {merged.map((c, i) => (
                <div key={`${c.post_id}-${i}`} style={{ marginBottom: 8 }}>
                  {c.author_username && (
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                      @{c.author_username}
                    </span>
                  )}
                  <p style={{
                    fontSize: 13, color: 'var(--color-text-secondary)',
                    margin: '2px 0 0', lineHeight: 1.5, whiteSpace: 'pre-wrap',
                    fontFamily: 'var(--font-body)',
                  }}>
                    {c.contribution_text}
                  </p>
                </div>
              ))}
            </div>
          );
        })()}
        {isRoot && post?.has_undefined_terms && post?.undefined_terms?.length > 0 && (
          <UndefinedTermsNotice terms={post.undefined_terms} postId={postId} onSubmit={onReplySubmit} />
        )}
        {isRoot && post?.has_no_claim && (
          <NoClaimNotice postId={postId} />
        )}
        {isRoot && post?.post_meta?.scrape_status === 'paywall' && !post?.post_meta?.pasted_content && (
          <ScrapeNotice type="paywall" postId={postId} />
        )}
        {isRoot && post?.post_meta?.scrape_status === 'unscrapeable' && (
          <ScrapeNotice type="unscrapeable" postId={postId} />
        )}
        {isRoot && post?.post_meta?.scrape_status === 'homepage' && (
          <ScrapeNotice type="homepage" postId={postId} />
        )}
        {isRoot && post?.scope_clarification && (
          <ScopeClarificationNotice notice={post.scope_clarification} postId={postId} />
        )}
        {isRoot && node?.evidence_checks?.length > 0 && (
          <div style={{
            marginTop: 12,
            borderTop: '1px solid #f1f5f9',
            paddingTop: 12,
          }}>
            <div style={{
              fontSize: 11,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 8,
              fontFamily: 'var(--font-body)',
            }}>
              Supporting Evidence
            </div>
            {node.evidence_checks.map(ec => (
              <div key={ec.id} style={{
                marginBottom: 6,
                padding: '6px 10px',
                background: '#f8fafc',
                borderRadius: 6,
                borderLeft: '3px solid #86efac',
              }}>
                <EvidenceItem ec={ec} postId={postId} />
              </div>
            ))}
          </div>
        )}
        {/* Sub-sprint 4 layout fix: IncidentSummaryCard lives inside the OP card
            (where Supporting Evidence sits, conceptually a peer section). Sub-sprint
            2 made incidents standalone children of root, so they need a render path
            that's independent of the directReplies/Responses flow — incidents are
            exhibits supporting the OP, not responses to it. */}
        {isRoot && node?.id && !node.is_definition_thread && (
          <IncidentSummaryCard
            parentArgId={node.id}
            user={user}
            rawSubmitReply={onReplySubmit}
            sourcePostId={postId}
          />
        )}
        {/* Sprint A Fix 3 subtree render: when the user navigates INTO a
            non-root node, HighlightedNode renders it as the page header — but
            EvidenceChallengeSection was only mounted inside ReplyCard, so
            evidence-challenge children (filtered out of
            /arguments/:id/replies by default) had nowhere to surface. June 2
            trace: navigating to node 37 showed "No responses yet" despite
            having child 39 as an evidence_challenge. Mounting the section
            here mirrors the ReplyCard pattern and gives the navigated-into
            view a place to show its EC children. */}
        {!isRoot && node?.id && !node.is_definition_thread
          && node.argument_type !== 'evidence_challenge' && (
          <EvidenceChallengeSection
            parentArgId={node.id}
            user={user}
            rawSubmitReply={onReplySubmit}
            sourcePostId={postId}
          />
        )}
      </div>
    </div>
  );
}

// ── Undefined terms notice ─────────────────────────────────────────────────
function UndefinedTermsNotice({ terms, postId, onSubmit }) {
  console.log('[DEBUG] UndefinedTermsNotice rendered, hasTerms:', Array.isArray(terms) && terms.length > 0, '| terms:', terms);
  const storageKey = `undefined-terms-dismissed-${postId}`;
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  const [adding, setAdding]       = React.useState(false);
  const [success, setSuccess]     = React.useState(false);
  const [selectedTerm, setSelectedTerm] = React.useState((terms && terms[0]) || '');
  const [defText, setDefText]     = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const defTextareaRef = React.useRef(null);

  // When the inline form opens, position cursor at the END of the pre-filled prefix
  // so any paste/type lands after "By X I mean " rather than before it.
  // setTimeout(0) ensures the textarea value is fully rendered before cursor placement.
  React.useEffect(() => {
    if (adding) {
      setTimeout(() => {
        const el = defTextareaRef.current;
        if (!el) return;
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }, 0);
    }
  }, [adding]);

  const dismiss = () => {
    try { localStorage.setItem(storageKey, 'true'); } catch {}
    setDismissed(true);
  };

  const openForm = (term) => {
    const t = term || (terms && terms[0]) || '';
    setSelectedTerm(t);
    setDefText(t ? `By ${t} I mean ` : '');
    setAdding(true);
    setSuccess(false);
  };

  const handleTermChange = (e) => {
    const t = e.target.value;
    setSelectedTerm(t);
    setDefText(`By ${t} I mean `);
  };

  const handleSubmitDef = async (e) => {
    e.preventDefault();
    if (!defText.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit?.({ content: defText });
      // Success: close inline form, show toast (don't permanently dismiss)
      setAdding(false);
      setDefText('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  if (dismissed) return null;
  if (!terms || terms.length === 0) return null;

  return (
    <div style={{
      marginTop: 12,
      background: '#f5f3ff',
      border: '1px solid #7c3aed',
      borderRadius: 8,
      padding: '9px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#4c1d95', fontFamily: 'var(--font-body)' }}>
            📖 Some terms may need definition for full evaluation
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {terms.map(t => (
              <span key={t} style={{
                display: 'inline-block', background: '#ede9fe',
                borderRadius: 4, padding: '1px 7px',
                fontSize: 11, fontWeight: 600, color: '#7c3aed',
              }}>{t}</span>
            ))}
          </div>

          {success && (
            <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#16a34a', fontFamily: 'var(--font-body)' }}>
              ✓ Definition submitted — analysis in progress
            </p>
          )}

          {!adding ? (
            <button
              onClick={() => openForm(terms[0])}
              style={{
                background: 'transparent', color: '#7c3aed',
                border: '1px solid #7c3aed',
                borderRadius: 5, padding: '4px 12px', fontSize: 12,
                fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
              }}
            >
              Add Definition
            </button>
          ) : (
            <form onSubmit={handleSubmitDef} style={{ marginTop: 4 }}>
              {terms.length > 1 && (
                <select
                  value={selectedTerm}
                  onChange={handleTermChange}
                  style={{
                    marginBottom: 6, fontSize: 12, borderRadius: 4,
                    border: '1px solid #7c3aed', padding: '2px 6px',
                    background: '#fff', color: '#4c1d95',
                    fontFamily: 'var(--font-body)', cursor: 'pointer',
                  }}
                >
                  {terms.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
              <textarea
                ref={defTextareaRef}
                value={defText}
                onChange={e => setDefText(e.target.value)}
                rows={2}
                style={{
                  width: '100%', fontSize: 13, borderRadius: 5,
                  border: '1px solid #7c3aed', padding: '5px 8px',
                  background: '#fff', color: '#1e1b4b',
                  fontFamily: 'var(--font-body)', resize: 'vertical',
                  display: 'block', boxSizing: 'border-box', marginBottom: 6,
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={submitting || !defText.trim()}
                  style={{
                    background: '#7c3aed', color: '#fff', border: 'none',
                    borderRadius: 5, padding: '4px 14px', fontSize: 12,
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
                    opacity: submitting || !defText.trim() ? 0.6 : 1,
                  }}
                >
                  {submitting ? 'Posting…' : 'Post Definition'}
                </button>
                <button type="button" onClick={() => setAdding(false)}
                  style={{
                    background: 'transparent', border: '1px solid #7c3aed',
                    color: '#7c3aed', borderRadius: 5, padding: '4px 10px',
                    fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
        <button
          onClick={dismiss}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#7c3aed', fontSize: 16, padding: 0, lineHeight: 1, flexShrink: 0,
          }}
          title="Dismiss"
        >×</button>
      </div>
    </div>
  );
}

// ── No-claim notice (FIX 1) ────────────────────────────────────────────────
function NoClaimNotice({ postId }) {
  const storageKey = `no-claim-dismissed-${postId}`;
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  if (dismissed) return null;
  return (
    <div style={{
      marginTop: 10,
      background: '#f0f9ff',
      border: '1px solid #7dd3fc',
      borderRadius: 8,
      padding: '8px 12px',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
    }}>
      <p style={{ margin: 0, fontSize: 12, color: '#0369a1', fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>
        <span style={{ fontWeight: 700 }}>❓ This post appears to be a question, not a claim.</span>{' '}
        Debates work best when a specific assertion is being made. Consider adding a statement like "I think…" or "The evidence shows…" so others know what to argue against.
      </p>
      <button
        onClick={() => { try { localStorage.setItem(storageKey, 'true'); } catch {} setDismissed(true); }}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: 14, padding: 0, lineHeight: 1, flexShrink: 0 }}
        title="Dismiss"
      >×</button>
    </div>
  );
}

// ── Scrape notice (FIX 9/10) ───────────────────────────────────────────────
function ScrapeNotice({ type, postId }) {
  const storageKey = `scrape-notice-dismissed-${type}-${postId}`;
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  if (dismissed) return null;

  const isPaywall  = type === 'paywall';
  const isHomepage = type === 'homepage';
  const bg     = isHomepage ? '#fffbeb' : isPaywall ? '#fffbeb' : '#f0f9ff';
  const border = isHomepage ? '#fcd34d' : isPaywall ? '#fcd34d' : '#7dd3fc';
  const color  = isHomepage ? '#92400e' : isPaywall ? '#92400e' : '#0369a1';
  const icon   = isHomepage ? '🏠' : isPaywall ? '🔒' : '⚠️';
  const msg    = isHomepage
    ? 'This link points to a homepage or category page rather than a specific article. Add a direct article URL, or describe what you are referencing in your post text.'
    : isPaywall
    ? 'This source is behind a paywall and could not be read automatically. Analysis is based on the post text only. If you have access, visit the Evidence page to paste the article content.'
    : 'This source could not be accessed automatically. Analysis is based on the post text only. If you have access to the article, visit the Evidence page to paste the content.';

  return (
    <div style={{
      marginTop: 10,
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: 8,
      padding: '8px 12px',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
    }}>
      <p style={{ margin: 0, fontSize: 12, color, fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>
        <span style={{ fontWeight: 700 }}>{icon} Source not read:</span>{' '}{msg}
      </p>
      <button
        onClick={() => { try { localStorage.setItem(storageKey, 'true'); } catch {} setDismissed(true); }}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color, fontSize: 14, padding: 0, lineHeight: 1, flexShrink: 0 }}
        title="Dismiss"
      >×</button>
    </div>
  );
}

// ── Scope clarification notice ─────────────────────────────────────────────
function ScopeClarificationNotice({ notice, postId }) {
  const storageKey = `scope-notice-dismissed-${postId}`;
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });

  if (dismissed || !notice) return null;

  return (
    <div style={{
      marginTop: 10,
      background: '#f0f9ff',
      border: '1px solid #7dd3fc',
      borderRadius: 8,
      padding: '8px 12px',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
    }}>
      <p style={{ margin: 0, fontSize: 12, color: '#0369a1', fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>
        <span style={{ fontWeight: 700 }}>📍 Ambiguous reference:</span> {notice}
      </p>
      <button
        onClick={() => {
          try { localStorage.setItem(storageKey, 'true'); } catch {}
          setDismissed(true);
        }}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#0369a1', fontSize: 14, padding: 0, lineHeight: 1, flexShrink: 0,
        }}
        title="Dismiss"
      >×</button>
    </div>
  );
}

// ── Card-level inline reply box ───────────────────────────────────────────────
const CardReplyBox = forwardRef(function CardReplyBox({ onSubmit, analyzing }, ref) {
  const [content, setContent]           = useState('');
  const [expanded, setExpanded]         = useState(false);
  const [imageFile, setImageFile]       = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading]       = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      setExpanded(true);
      setTimeout(() => textareaRef.current?.focus(), 30);
    },
  }));

  // Warn before unload when there is unsaved text
  useEffect(() => {
    if (!content && !imageFile) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [content, imageFile]);

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const cardCharOver = content.length > 3000;
  const cardCharNearLimit = content.length > 2800 && !cardCharOver;

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!content.trim() && !imageFile) return;
    if (cardCharOver) return;
    if (content.trim() && isLinkOnly(content)) {
      setError("Add your argument — what does this link prove?");
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let imageData = {};
      if (imageFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('image', imageFile);
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploading(false);
        imageData = {
          image_url:        uploadRes.data.url,
          image_public_id:  uploadRes.data.public_id,
          image_ai_score:   uploadRes.data.ai_detection?.ai_score ?? null,
          image_ai_flagged: uploadRes.data.ai_detection?.is_flagged ?? false,
        };
      }
      const result = await onSubmit({ content, ...imageData });
      if (result === true) {
        setContent('');
        clearImage();
        setExpanded(false);
      } else if (result && typeof result === 'object' && result.rejected) {
        // Rejection — message shown globally via analysisMessage. Preserve text.
      } else {
        // Show 429/error inline without clearing input — use `message` for natural-language errors
        setError(typeof result === 'string' ? result : 'Failed to submit response');
      }
    } catch (err) {
      setUploading(false);
      setError('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const hasContent = content.trim().length > 0 || imageFile;

  return (
    <div style={{ marginTop: 10 }} onClick={e => e.stopPropagation()}>
      <form onSubmit={handleSubmit}>
        <div style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 6, padding: '6px 10px',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <textarea
            ref={textareaRef}
            data-reply-box="true"
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--color-text-primary)', fontSize: 16, resize: 'none', width: '100%',
              lineHeight: 1.5, fontFamily: 'var(--font-body)',
              height: expanded ? 72 : 24, transition: 'height 0.15s ease',
            }}
            placeholder="Add your argument..."
            value={content}
            onChange={e => { setContent(e.target.value); if (e.target.value) setExpanded(true); }}
            onFocus={() => setExpanded(true)}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if ((content.trim() || imageFile) && !submitting && !uploading && !cardCharOver) handleSubmit(e);
              }
            }}
          />
          {imagePreview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <img src={imagePreview} alt="preview" style={{ height: 36, width: 48, objectFit: 'cover', borderRadius: 3, border: '1px solid var(--color-border)' }} />
              <button type="button" onClick={clearImage}
                style={{
                  background: 'var(--color-border)', border: 'none', borderRadius: '50%',
                  color: 'var(--color-text-primary)', width: 16, height: 16, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <X size={9} />
              </button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" onClick={() => fileInputRef.current?.click()}
                style={{
                  background: 'transparent', border: '1px solid var(--color-border)',
                  borderRadius: 4, color: 'var(--color-text-muted)', padding: '2px 6px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                }}>
                <ImagePlus size={12} />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
              {uploading && <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>Uploading...</span>}
              {content.length > 0 && (
                <span style={{ fontSize: 10, fontFamily: 'var(--font-body)', color: cardCharOver ? 'var(--color-invalidated)' : cardCharNearLimit ? 'var(--color-disputed)' : 'var(--color-text-muted)' }}>
                  {content.length} / 3000
                </span>
              )}
            </div>
            {hasContent && (
              <button type="submit" disabled={submitting || uploading || analyzing || cardCharOver}
                style={{
                  padding: '3px 12px', background: 'var(--color-brand)', color: '#fff',
                  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  fontWeight: 600, fontFamily: 'var(--font-body)',
                }}>
                {submitting ? '...' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </form>
      {error && <p style={{ color: 'var(--color-invalidated)', fontSize: 12, margin: '4px 0 0', fontFamily: 'var(--font-body)' }}>{error}</p>}
    </div>
  );
});

// ── Card-level defense box (amber — for red/challenged arguments) ─────────────
const CardDefenseBox = forwardRef(function CardDefenseBox({ onSubmit }, ref) {
  const [content, setContent]           = useState('');
  const [expanded, setExpanded]         = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState('');
  const textareaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => { setExpanded(true); setTimeout(() => textareaRef.current?.focus(), 30); },
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!content.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await onSubmit({ content });
      if (result === true) { setContent(''); setExpanded(false); }
      else setError(result);
    } catch { setError('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{ marginTop: 10 }} onClick={e => e.stopPropagation()}>
      <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-body)' }}>
        Argument currently losing
      </p>
      <form onSubmit={handleSubmit}>
        <div style={{
          background: 'var(--color-bg)',
          border: '1px solid #f59e0b',
          borderRadius: 6, padding: '6px 10px',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <textarea
            ref={textareaRef}
            data-reply-box="true"
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--color-text-primary)', fontSize: 16, resize: 'none', width: '100%',
              lineHeight: 1.5, fontFamily: 'var(--font-body)',
              height: expanded ? 72 : 24, transition: 'height 0.15s ease',
            }}
            placeholder="This argument has been challenged — add evidence to defend it..."
            value={content}
            onChange={e => { setContent(e.target.value); if (e.target.value) setExpanded(true); }}
            onFocus={() => setExpanded(true)}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if (content.trim() && !submitting) handleSubmit(e);
              }
            }}
          />
          {content.trim().length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" disabled={submitting}
                style={{
                  padding: '3px 12px', background: '#f59e0b', color: '#fff',
                  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  fontWeight: 600, fontFamily: 'var(--font-body)',
                }}>
                {submitting ? '...' : 'Add defense'}
              </button>
            </div>
          )}
        </div>
      </form>
      {error && <p style={{ color: 'var(--color-invalidated)', fontSize: 12, margin: '4px 0 0', fontFamily: 'var(--font-body)' }}>{error}</p>}
    </div>
  );
});

// ── Incident summary card (nested inside a ReplyCard for arguments with incidents) ──
const STATUS_DOT = { green: 'var(--color-verified)', neutral: 'var(--color-disputed)', red: 'var(--color-invalidated)' };

function IncidentSummaryCard({ parentArgId, user, rawSubmitReply, sourcePostId }) {
  const [data, setData] = useState(null); // null = loading
  const [expandedAll, setExpandedAll] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/arguments/${parentArgId}/incidents`)
      .then(r => setData(r.data))
      .catch(() => setData({ incidents: [], total: 0 }));
  }, [parentArgId]);

  if (!data || data.total === 0) return null;

  const { incidents, total, green_count, neutral_count, red_count } = data;
  const showSummaryCard = total >= 5;
  const visibleIncidents = expandedAll ? incidents : incidents.slice(0, 5);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const statusLabel = (status) => ({ green: 'confirmed', neutral: 'disputed', red: 'challenged' }[status] || 'disputed');

  const renderIncidentLine = (incident) => {
    const isExpanded = expandedIds.has(incident.id);
    const dotColor = STATUS_DOT[incident.status] || STATUS_DOT.neutral;
    return (
      <div key={incident.id} style={{ borderTop: '1px solid var(--color-border)' }}>
        <div
          onClick={() => navigate(`/incidents/${incident.id}`)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 0', cursor: 'pointer',
          }}
        >
          <span style={{ color: dotColor, fontSize: 8, flexShrink: 0 }}>●</span>
          <span style={{
            flex: 1, fontSize: 12, color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-body)', lineHeight: 1.4,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {(incident.synopsis || '').slice(0, 80)}{(incident.synopsis || '').length > 80 ? '…' : ''}
          </span>
          {incident.category_label && (
            <CategoryPill label={incident.category_label} />
          )}
          {incident.source_count > 0 && (
            <span style={{ fontSize: 10, color: 'var(--color-text-muted)', flexShrink: 0, fontFamily: 'var(--font-body)' }}>
              {incident.source_count} source{incident.source_count !== 1 ? 's' : ''}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleExpand(incident.id); }}
            aria-label={isExpanded ? 'Collapse incident' : 'Expand incident'}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--color-text-muted)', fontSize: 12, flexShrink: 0,
              padding: '2px 6px', fontFamily: 'var(--font-body)',
            }}
          >
            {isExpanded ? '▲' : '▼'}
          </button>
        </div>
        {isExpanded && (
          <div style={{
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 6, padding: '8px 10px', marginBottom: 6,
          }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--color-text-primary)', fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>
              {incident.synopsis}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: incident.top_sources?.length ? 6 : 0, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 10, fontWeight: 700, color: dotColor,
                background: dotColor + '22', padding: '1px 6px', borderRadius: 8,
                fontFamily: 'var(--font-body)',
              }}>
                {statusLabel(incident.status)}
              </span>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                {incident.reply_count > 0 && `${incident.reply_count} repl${incident.reply_count !== 1 ? 'ies' : 'y'}`}
              </span>
            </div>
            {incident.top_sources?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                {incident.top_sources.map((src, i) => src.evidence_url ? (
                  <a key={i} href={src.evidence_url} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{
                      fontSize: 10, color: 'var(--color-brand)',
                      background: 'var(--color-surface-hover)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 8, padding: '1px 7px',
                      textDecoration: 'none', fontFamily: 'var(--font-body)',
                    }}>
                    🔗 {(() => { try { return new URL(src.evidence_url).hostname.replace(/^www\./, ''); } catch { return src.evidence_url.slice(0, 20); } })()}
                  </a>
                ) : (
                  <span key={i} style={{ fontSize: 10, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                    {(src.evidence_text || '').slice(0, 30)}
                  </span>
                ))}
              </div>
            )}
            {user && rawSubmitReply && (
              <IncidentReplyBox
                incidentId={incident.id}
                onSubmit={rawSubmitReply}
                sourcePostId={sourcePostId}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  if (!showSummaryCard) {
    // Fewer than 5 incidents — show inline without header
    return (
      <div style={{
        marginTop: 8, marginBottom: 4,
        border: '1px solid var(--color-border)',
        borderRadius: 6, padding: '6px 10px',
        background: 'var(--color-surface-hover)',
      }}>
        <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-body)' }}>
          Supporting incidents
        </p>
        {incidents.map(renderIncidentLine)}
      </div>
    );
  }

  // Summary card — 5+ incidents
  return (
    <div style={{
      marginTop: 8, marginBottom: 4,
      border: '1px solid var(--color-border)',
      borderRadius: 6,
      background: 'var(--color-surface-hover)',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-primary)', fontFamily: 'var(--font-body)' }}>
            📋 {total} supporting incident{total !== 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
            {green_count > 0 && <span style={{ color: 'var(--color-verified)' }}>{green_count} confirmed</span>}
            {green_count > 0 && (neutral_count > 0 || red_count > 0) && ' · '}
            {neutral_count > 0 && <span style={{ color: 'var(--color-disputed)' }}>{neutral_count} disputed</span>}
            {neutral_count > 0 && red_count > 0 && ' · '}
            {red_count > 0 && <span style={{ color: 'var(--color-invalidated)' }}>{red_count} challenged</span>}
          </span>
        </div>
      </div>
      <div style={{ padding: '2px 10px 6px' }}>
        {visibleIncidents.map(renderIncidentLine)}
        {total > 5 && !expandedAll && (
          <button
            onClick={() => setExpandedAll(true)}
            style={{
              background: 'transparent', border: 'none', color: 'var(--color-brand)',
              fontSize: 11, cursor: 'pointer', padding: '4px 0',
              fontFamily: 'var(--font-body)', fontWeight: 500,
            }}
          >
            and {total - 5} more incident{total - 5 !== 1 ? 's' : ''} →
          </button>
        )}
        {expandedAll && total > 5 && (
          <button
            onClick={() => setExpandedAll(false)}
            style={{
              background: 'transparent', border: 'none', color: 'var(--color-text-muted)',
              fontSize: 11, cursor: 'pointer', padding: '4px 0',
              fontFamily: 'var(--font-body)',
            }}
          >
            ▲ Show fewer
          </button>
        )}
      </div>
    </div>
  );
}

// Compact reply box for challenging a specific incident (inside IncidentSummaryCard)
function IncidentReplyBox({ incidentId, onSubmit, sourcePostId }) {
  const [content, setContent] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!content.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await onSubmit({ content, parent_argument_id: incidentId });
      if (result === true) { setContent(''); setExpanded(false); }
      else if (result && typeof result === 'object' && result.rejected) { /* handled globally */ }
      else setError(typeof result === 'string' ? result : 'Failed to submit');
    } catch { setError('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
      <textarea
        rows={expanded ? 2 : 1}
        style={{
          width: '100%', background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 4, color: 'var(--color-text-primary)',
          fontSize: 12, padding: '4px 8px', resize: 'none',
          fontFamily: 'var(--font-body)', boxSizing: 'border-box',
          height: expanded ? 52 : 26, transition: 'height 0.15s',
        }}
        placeholder="Challenge this incident..."
        value={content}
        onChange={e => { setContent(e.target.value); if (e.target.value) setExpanded(true); }}
        onFocus={() => setExpanded(true)}
      />
      {expanded && content.trim().length > 0 && (
        <div style={{ marginTop: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={submitting}
            style={{
              padding: '3px 10px', background: 'var(--color-brand)', color: '#fff',
              border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11,
              fontWeight: 600, fontFamily: 'var(--font-body)',
            }}>
            {submitting ? '...' : 'Submit'}
          </button>
        </div>
      )}
      {error && <p style={{ color: 'var(--color-invalidated)', fontSize: 11, margin: '3px 0 0', fontFamily: 'var(--font-body)' }}>{error}</p>}
    </form>
  );
}

// ── Evidence challenge section (nested inside a ReplyCard) ───────────────────
function EvidenceChallengeSection({ parentArgId, user, rawSubmitReply, sourcePostId }) {
  const [challenges, setChallenges] = useState(null); // null = not loaded yet
  const [expanded, setExpanded] = useState(false);
  const [count, setCount] = useState(0);

  // Fetch count on mount (just use the full list since we need count anyway)
  useEffect(() => {
    api.get(`/arguments/${parentArgId}/replies?type=evidence_challenge`)
      .then(r => {
        const list = r.data.replies || [];
        setCount(list.length);
        // Pre-load data if already fetched
        if (list.length > 0) setChallenges(list);
      })
      .catch(() => {});
  }, [parentArgId]);

  if (count === 0 && challenges !== null) return null;
  if (count === 0) return null;

  const toggle = () => {
    setExpanded(prev => !prev);
    if (!challenges) {
      api.get(`/arguments/${parentArgId}/replies?type=evidence_challenge`)
        .then(r => setChallenges(r.data.replies || []))
        .catch(() => setChallenges([]));
    }
  };

  return (
    <div
      style={{
        marginTop: 10,
        borderTop: '1px solid var(--color-border)',
        paddingTop: 8,
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Toggle button */}
      <button
        onClick={toggle}
        style={{
          background: 'transparent', border: 'none',
          color: 'var(--color-text-muted)', fontSize: 11,
          cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 4,
          fontFamily: 'var(--font-body)', fontWeight: 600,
        }}
      >
        <span>⚡</span>
        <span>{count} evidence challenge{count !== 1 ? 's' : ''}</span>
        <span>{expanded ? '▴' : '▾'}</span>
      </button>

      {/* Expanded challenge cards */}
      {expanded && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {challenges === null ? (
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>Loading...</p>
          ) : challenges.length === 0 ? null : (
            challenges.map(ch => (
              <EvidenceChallengeCard
                key={ch.id}
                challenge={ch}
                user={user}
                rawSubmitReply={rawSubmitReply}
                sourcePostId={sourcePostId}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceChallengeCard({ challenge, user, rawSubmitReply, sourcePostId }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const statusDot = { green: 'var(--color-verified)', red: 'var(--color-invalidated)', neutral: 'var(--color-text-muted)' }[challenge.status] || 'var(--color-text-muted)';

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      // rawSubmitReply is handleReplySubmit from PostDetail — calls /posts/:id/respond
      const result = rawSubmitReply
        ? await rawSubmitReply({ content, parent_argument_id: challenge.id })
        : 'No submit handler';
      if (result === true) { setContent(''); setReplyOpen(false); }
      else setError(typeof result === 'string' ? result : 'Failed to submit');
    } catch { setError('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{
      marginLeft: 12,
      background: 'var(--color-surface-hover)',
      border: '1px solid var(--color-border)',
      borderLeft: `3px solid ${statusDot}`,
      borderRadius: 6,
      padding: '7px 10px',
      fontSize: 12,
    }}>
      {/* Label + author */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          color: '#b45309', background: '#fef3c7',
          border: '1px solid #fcd34d',
          padding: '1px 6px', borderRadius: 8,
          fontFamily: 'var(--font-body)',
        }}>⚡ Evidence Challenge</span>
        {challenge.author_username && (
          <span style={{ fontSize: 10, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
            by {challenge.author_username}<FlagIcon countryCode={challenge.author_country_code} />
          </span>
        )}
      </div>
      {/* Synopsis */}
      {challenge.synopsis && (
        <p style={{
          margin: '0 0 4px', fontSize: 12, lineHeight: 1.4,
          color: 'var(--color-text-primary)', fontFamily: 'var(--font-body)', fontWeight: 600,
        }}>
          {challenge.synopsis}
        </p>
      )}
      {challenge.status_reasoning && (
        <p style={{ margin: '0 0 4px', fontSize: 11, color: 'var(--color-text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-body)' }}>
          {challenge.status_reasoning}
        </p>
      )}
      {/* Reply toggle */}
      {user && (
        <button
          onClick={() => setReplyOpen(o => !o)}
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--color-brand)', fontSize: 10,
            cursor: 'pointer', padding: 0, marginTop: 2,
            fontFamily: 'var(--font-body)', fontWeight: 500,
          }}
        >
          {replyOpen ? 'Cancel' : 'Reply'}
        </button>
      )}
      {replyOpen && (
        <form onSubmit={handleReplySubmit} style={{ marginTop: 6 }}>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={2}
            placeholder="Respond to this evidence challenge..."
            style={{
              width: '100%', background: 'var(--color-bg)',
              border: '1px solid var(--color-border)', borderRadius: 4,
              color: 'var(--color-text-primary)', fontSize: 12,
              padding: '4px 8px', fontFamily: 'var(--font-body)',
              resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          {error && <p style={{ color: 'var(--color-invalidated)', fontSize: 11, margin: '2px 0', fontFamily: 'var(--font-body)' }}>{error}</p>}
          <button
            type="submit"
            disabled={submitting || !content.trim()}
            style={{
              marginTop: 4, padding: '3px 10px',
              background: 'var(--color-brand)', color: '#fff',
              border: 'none', borderRadius: 4, cursor: 'pointer',
              fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
            }}
          >
            {submitting ? '...' : 'Submit'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Reply card ────────────────────────────────────────────────────────────────
const REPLY_STATUS_BG = {
  green:   'var(--color-verified-bg)',
  red:     'var(--color-invalidated-bg)',
  neutral: 'var(--color-surface)',
  // 2026-05-12-e — disputed node status: amber/orange background indicating the
  // rebuttal has been credibly challenged but not yet defeated. Sits between
  // neutral and red visually.
  disputed: 'var(--color-disputed-bg, rgba(217,119,6,0.10))',
};

// ── Grouped reply list ────────────────────────────────────────────────────
// FIX 1: Recursive tree renderer — renders a nested tree of argument nodes with indentation.
// Used when the full argument tree is available from the initial load.
function ReplyTree({ nodes, depth = 0, user, clickViewThread, handleCardReplySubmit, rawSubmitReply, sourcePostId, highlightArgId }) {
  if (!nodes || nodes.length === 0) return null;
  return (
    <div style={{
      marginLeft: depth > 0 ? 16 : 0,
      borderLeft: depth > 0 ? '2px solid var(--color-border)' : 'none',
      paddingLeft: depth > 0 ? 12 : 0,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {nodes.map(node => (
        <div key={node.id}>
          <ReplyCard
            node={node}
            isOpinion={node.opinion_thread}
            user={user}
            onViewThread={() => clickViewThread(node)}
            onSubmitReply={handleCardReplySubmit(node)}
            rawSubmitReply={rawSubmitReply}
            sourcePostId={sourcePostId}
            highlightArgId={highlightArgId}
          />
          {node.children && node.children.length > 0 && (
            <ReplyTree
              nodes={node.children}
              depth={depth + 1}
              user={user}
              clickViewThread={clickViewThread}
              handleCardReplySubmit={handleCardReplySubmit}
              rawSubmitReply={rawSubmitReply}
              sourcePostId={sourcePostId}
              highlightArgId={highlightArgId}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ReplyGroupList({ groups, postId, user, clickViewThread, handleCardReplySubmit, rawSubmitReply, sourcePostId, highlightArgId, isRoot }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.map((group, i) => (
        <React.Fragment key={group.key}>
          <ReplyGroup
            group={group}
            postId={postId}
            user={user}
            clickViewThread={clickViewThread}
            handleCardReplySubmit={handleCardReplySubmit}
            rawSubmitReply={rawSubmitReply}
            sourcePostId={sourcePostId}
            highlightArgId={highlightArgId}
          />
          {isRoot && i === 0 && <AdPlaceholder slot="postdetail-mid" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function ReplyGroup({ group, postId, user, clickViewThread, handleCardReplySubmit, rawSubmitReply, sourcePostId, highlightArgId }) {
  const storageKey = `group-collapsed-${postId}-${group.key}`;
  const [collapsed, setCollapsed] = React.useState(() => {
    if (group.key === 'definitions') return false; // always default expanded
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) return stored === 'true';
    } catch {}
    return !group.defaultExpanded;
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  };

  return (
    <div>
      {/* Group header */}
      <button
        onClick={toggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 'none',
          borderBottom: '1px solid var(--color-border)',
          paddingBottom: 8,
          marginBottom: collapsed ? 0 : 10,
          cursor: 'pointer',
          textAlign: 'left',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        <span style={{ fontSize: 14 }}>{group.icon}</span>
        <span style={{
          flex: 1,
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-body)',
        }}>
          {group.title}
          {group.key !== 'definitions' && (
            <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: 6 }}>
              ({group.items.length})
            </span>
          )}
        </span>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
          {collapsed ? '▾' : '▴'}
        </span>
      </button>

      {/* Cards — each top-level card, plus its descendant subtree below if
          present. (B) consolidation: items at root come from fullArgTree
          with .children populated by buildTree; at non-root they come from
          directReplies (flat, no .children) and the nested ReplyTree
          no-ops via the empty-array guard inside ReplyTree. Descendants
          are NOT regrouped — they stay nested under their parent regardless
          of their own status. */}
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderLeft: '2px solid #e2e8f0', paddingLeft: 12, marginLeft: 4 }}>
          {group.items.map(reply => (
            <div key={reply.id}>
              <ReplyCard
                node={reply}
                isOpinion={reply.opinion_thread}
                user={user}
                onViewThread={() => clickViewThread(reply)}
                onSubmitReply={handleCardReplySubmit(reply)}
                rawSubmitReply={rawSubmitReply}
                sourcePostId={sourcePostId}
                highlightArgId={highlightArgId}
              />
              {reply.children && reply.children.length > 0 && (
                <ReplyTree
                  nodes={reply.children}
                  depth={1}
                  user={user}
                  clickViewThread={clickViewThread}
                  handleCardReplySubmit={handleCardReplySubmit}
                  rawSubmitReply={rawSubmitReply}
                  sourcePostId={sourcePostId}
                  highlightArgId={highlightArgId}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReplyCard({ node, isOpinion, user, onViewThread, onSubmitReply, rawSubmitReply, sourcePostId, highlightArgId }) {
  const canEdit = user && user.username === node.author_username && String(node.source_post_id) !== String(sourcePostId);
  const inlineBoxRef = useRef(null);
  const cardBg = REPLY_STATUS_BG[node.status] || 'var(--color-surface)';

  // Debug merge group evidence

  const [cardExpanded, setCardExpanded] = useState(false);
  const [inlineIncidents, setInlineIncidents] = useState([]);
  const [argEvidenceChecks, setArgEvidenceChecks] = useState([]);
  const synopsisIsFullContent = isSimilar(node.synopsis, node.full_content);
  // Only show merge members that actually merged into THIS card's argument
  const relevantMerges = node.merge_members?.filter(m => String(m.merged_into) === String(node.id)) || [];
  const [editing, setEditing]               = useState(false);
  const [editText, setEditText]             = useState('');
  const [editLoading, setEditLoading]       = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError]           = useState('');
  const [editDone, setEditDone]             = useState(false);
  const [replyContributors, setReplyContributors] = useState([]);

  useEffect(() => {
    if (node.reply_count > 0) {
      api.get(`/arguments/${node.id}/replies?limit=2`)
        .then(r => {
          const replies = r.data.replies || [];
          const others = [...new Set(replies.map(r => r.author_username))]
            .filter(u => u && u !== node.author_username).slice(0, 2);
          setReplyContributors(others);
        })
        .catch(() => {});
    }
  }, [node.id, node.reply_count]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cardExpanded) return;
    api.get(`/arguments/${node.id}/incidents`)
      .then(r => setInlineIncidents(r.data.incidents || []))
      .catch(() => {});
    api.get(`/arguments/${node.id}/evidence`)
      .then(r => setArgEvidenceChecks(r.data.evidence || []))
      .catch(() => {});
  }, [cardExpanded, node.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditClick = async () => {
    setEditLoading(true);
    try {
      const res = await api.get(`/posts/${node.source_post_id}`);
      setEditText(res.data.post?.content || node.full_content || '');
    } catch {
      setEditText(node.full_content || '');
    } finally {
      setEditLoading(false);
      setEditing(true);
    }
  };

  const handleEditSubmit = async () => {
    if (!editText.trim()) return;
    setEditSubmitting(true);
    setEditError('');
    try {
      await api.post(`/posts/${node.source_post_id}/reprocess`, { content: editText });
      setEditing(false);
      setEditDone(true);
      setTimeout(() => setEditDone(false), 8000);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save');
    } finally {
      setEditSubmitting(false);
    }
  };

  const isHighlighted = highlightArgId === node.id;
  const shareUrl = sourcePostId
    ? `${window.location.origin}/posts/${sourcePostId}/a/${node.id}`
    : window.location.href;

  const statusBorderColor = node.is_definition_thread
    ? '#7c3aed'
    : node.status === 'green' ? 'var(--color-verified-bar)'
    : node.status === 'red'   ? 'var(--color-invalidated-bar)'
    : node.status === 'disputed' ? 'var(--color-disputed-bar, #d97706)' // VC4 fix-up: disputed node styling
    : 'var(--color-neutral-bar)';

  // Minimized header: synopsis_medium or synopsis + source domain pills + chevron
  const sourceDomains = (node.evidence || [])
    .filter(ev => ev.evidence_url)
    .map(ev => getDomain(ev.evidence_url))
    .filter((d, i, arr) => arr.indexOf(d) === i)
    .slice(0, 3);

  return (
    <div
      id={node.id ? `arg-${node.id}` : undefined}
      style={{
        background: node.is_definition_thread ? '#faf5ff' : cardBg,
        border: node.is_definition_thread ? '1px solid #c4b5fd' : '1px solid var(--color-border)',
        borderLeft: `3px solid ${statusBorderColor}`,
        borderRadius: 8,
        overflow: 'hidden',
        opacity: node.edited_away ? 0.6 : 1,
        outline: isHighlighted ? '2px solid var(--color-brand)' : 'none',
        transition: 'outline 0.3s',
      }}
    >
      {/* Minimized header — title navigates, chevron toggles, rest is inert.
          Pre-fix the entire header was a single click-to-toggle target which
          left leaf replies (reply_count === 0, no "View N replies →" button)
          with no navigation affordance at all. */}
      <div
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '10px 12px',
          borderBottom: cardExpanded ? '1px solid var(--color-border)' : 'none',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            onClick={(e) => {
              // Skip navigation when the user is selecting text — either a
              // click-drag (mousedown→mouseup with movement) or an existing
              // selection at click time. window.getSelection covers both
              // cases because a drag leaves a non-empty selection at mouseup.
              try {
                const sel = window.getSelection && window.getSelection();
                if (sel && sel.toString().length > 0) return;
              } catch {}
              e.stopPropagation();
              onViewThread();
            }}
            style={{
              fontSize: 13, color: '#1e293b', fontWeight: 500, lineHeight: 1.4,
              marginBottom: sourceDomains.length > 0 ? 4 : 0,
              cursor: 'pointer',
              userSelect: 'text',
            }}
          >
            {node.synopsis_medium || node.synopsis}
          </div>
          {sourceDomains.length > 0 && (
            <div>
              {sourceDomains.map(domain => (
                <span key={domain} style={{ fontSize: 11, color: '#64748b', marginRight: 8, fontFamily: 'var(--font-body)' }}>
                  🔗 {domain}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCardExpanded(x => !x); }}
          aria-label={cardExpanded ? 'Collapse reply' : 'Expand reply'}
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: '#94a3b8', flexShrink: 0, marginTop: 2,
            display: 'flex', alignItems: 'center',
          }}
        >
          <ChevronDown
            size={14}
            style={{
              transform: cardExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        </button>
      </div>

      {/* Expanded full content */}
      {cardExpanded && (
      <div style={{ padding: '10px 14px' }}>
        {node.edited_away && (
          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', fontStyle: 'italic', marginBottom: 8, margin: '0 0 8px', fontFamily: 'var(--font-body)' }}>
            This argument was removed in an edit. Its reply threads are preserved.
          </p>
        )}
        {editDone && (
          <p style={{ fontSize: 12, color: 'var(--color-verified)', marginBottom: 8, margin: '0 0 8px', fontFamily: 'var(--font-body)' }}>
            Re-analyzing your edit... The debate will update shortly.
          </p>
        )}

        {/* Discredited label — shown above badge row for red (challenged/invalidated) args */}
        {node.status === 'red' && !node.is_definition_thread && (
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
            color: 'var(--color-invalidated)', marginBottom: 6,
            fontFamily: 'var(--font-body)',
          }}>
            Discredited
          </div>
        )}

        {/* Header row — badges only; username lives in contributor avatar row below */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          {node.is_definition_thread ? (
            <>
              <span style={pill('#7c3aed', '#ede9fe', '#7c3aed')}>📖 Definition</span>
              {node.defines_term && (
                <span style={{ fontSize: 11, color: '#6d28d9', fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                  Defining: {node.defines_term}
                </span>
              )}
            </>
          ) : isOpinion ? (
            <span style={pill('var(--color-text-muted)', 'var(--color-surface-hover)', 'var(--color-border)')}>Opinion</span>
          ) : (
            <span style={pill(
              node.stance === 'affirmative' ? '#0d9488' : '#ffffff',
              node.stance === 'affirmative' ? '#ccfbf1' : '#1e293b',
              node.stance === 'affirmative' ? '#0d9488' : '#1e293b',
            )}>
              {node.stance === 'affirmative' ? 'Affirmative' : 'Rebuttal'}
            </span>
          )}
          {/* Definition challenge badge */}
          {node.definition_challenge_type && (
            <span style={{
              display: 'inline-block',
              background: 'rgba(217,119,6,0.12)',
              border: '1px solid rgba(217,119,6,0.4)',
              borderRadius: 4,
              padding: '1px 7px',
              fontSize: 11,
              fontWeight: 700,
              color: '#92400e',
              marginLeft: 4,
            }}>
              ⚠️ Challenges: {node.definition_challenge_type.replace(/_/g, ' ')}
            </span>
          )}
          {node.decisive && node.status === 'green' && (
            <span style={pill('var(--color-disputed)', 'var(--color-disputed-bg)', 'var(--color-disputed-border)')}>
              ★ Key argument
            </span>
          )}
          {(node.evidence?.length || 0) >= 2 && (
            <span style={{ fontSize: 10, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)', letterSpacing: '0.01em' }}>
              {node.evidence.length} sources
            </span>
          )}
          <div className="card-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShareButton url={shareUrl} title={node.synopsis?.slice(0, 80) || ''} />
            {canEdit && !editing && (
              <button
                onClick={e => { e.stopPropagation(); handleEditClick(); }}
                disabled={editLoading}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-muted)', cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'center', gap: 3,
                  fontSize: 11, fontFamily: 'var(--font-body)',
                }}
              >
                <Pencil size={11} />
                {editLoading ? 'Loading...' : 'Edit'}
              </button>
            )}
          </div>
        </div>

        {/* Contributor avatars — primary author + merged authors stacked */}
        {!editing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            {(() => {
              // Collect merged authors — only from args that merged into THIS card
              const mergedAuthors = relevantMerges.length > 0
                ? [...new Set(relevantMerges.map(m => m.author_username).filter(u => u && u !== node.author_username))]
                : [];
              const stackAuthors = mergedAuthors.slice(0, 3);
              const extraCount = mergedAuthors.length - 3;
              return (
                <>
                  {/* Primary author */}
                  <Avatar username={node.author_username || '?'} size={24} />
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                    {node.author_username || 'Unknown'}
                    <FlagIcon countryCode={node.author_country_code} />
                  </span>
                  {/* Stacked merged author avatars */}
                  {stackAuthors.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {stackAuthors.map((author, i) => (
                        <div key={author} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 3 - i }}>
                          <Avatar username={author} size={20} />
                        </div>
                      ))}
                      {extraCount > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 4, fontFamily: 'var(--font-body)' }}>
                          +{extraCount}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Reply contributors */}
                  {replyContributors.map(u => (
                    <React.Fragment key={u}>
                      <Avatar username={u} size={24} />
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>{u}</span>
                    </React.Fragment>
                  ))}
                </>
              );
            })()}
          </div>
        )}

        {node.full_content && !synopsisIsFullContent && !editing && (
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 6, lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>{node.full_content}</p>
        )}
        {/* Merge group members — always visible when card is expanded */}
        {relevantMerges.length > 0 && !editing && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ marginTop: 4, borderLeft: '2px solid var(--color-border)', paddingLeft: 10 }}>
                {relevantMerges.map((member) => (
                  member.full_content && member.full_content !== node.full_content ? (
                    <div key={member.id} style={{ marginBottom: 8 }}>
                      {member.author_username && member.author_username !== node.author_username && (
                        <span style={{ fontSize: 10, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                          {member.author_username}
                        </span>
                      )}
                      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '2px 0 0', lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>
                        {member.full_content}
                      </p>
                    </div>
                  ) : null
                ))}
              </div>
          </div>
        )}
        {/* Sprint A Fix A: surface merged contributions. Pre-Sprint-A,
            affirmative-merge replies (responses 50/52/51b in the June-2 post-47
            trace) updated post_node_contributions silently — no UI signal. The
            text was preserved in the DB but the user saw nothing on the parent
            card. Now: every contribution with is_originator=false renders here
            as a short "also raised" item showing raw user text + author. The
            originator content is already rendered above; this list is purely
            additive merged contributions. */}
        {(() => {
          const merged = (node.contributions || []).filter(c => !c.is_originator && c.contribution_text);
          if (merged.length === 0 || editing) return null;
          return (
            <div style={{
              marginTop: 8, marginBottom: 6,
              borderLeft: '2px solid var(--color-border)', paddingLeft: 10,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: 'var(--color-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                marginBottom: 4, fontFamily: 'var(--font-body)',
              }}>
                Also raised ({merged.length})
              </div>
              {merged.map((c, i) => (
                <div key={`${c.post_id}-${i}`} style={{ marginBottom: 6 }}>
                  {c.author_username && (
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                      @{c.author_username}
                    </span>
                  )}
                  <p style={{
                    fontSize: 12, color: 'var(--color-text-secondary)',
                    margin: '2px 0 0', lineHeight: 1.5, whiteSpace: 'pre-wrap',
                    fontFamily: 'var(--font-body)',
                  }}>
                    {c.contribution_text}
                  </p>
                </div>
              ))}
            </div>
          );
        })()}
        {/* Sprint A Phase 2 (Fix 4): replaces the "Sources:" pill row with
            IncidencesList — each chip carries a source-origin badge and a
            "⚠ Challenged" badge when the evidence is_challenged, and clicks
            route to the EvidencePage (where the challenge composer lives)
            instead of opening the raw URL. The pre-Phase-2 behavior of
            opening URLs in a new tab is preserved by the in-card link
            already rendered inside EvidenceItem on the expanded card; the
            Sources row's new job is to give users a path to challenge. */}
        {!editing && (
          <IncidencesList
            evidence={node.evidence}
            postId={sourcePostId}
            stopPropagation={true}
          />
        )}
        {inlineIncidents.length > 0 && !editing && (
          <div style={{ marginBottom: 6 }}>
            {inlineIncidents.map(incident => (
              <div key={incident.id} style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: 10, marginTop: 6, fontSize: 13, color: '#374151' }}>
                <span style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginRight: 6, fontFamily: 'var(--font-body)' }}>Evidence</span>
                {(incident.synopsis || '').slice(0, 100)}{(incident.synopsis || '').length > 100 ? '…' : ''}
              </div>
            ))}
          </div>
        )}
        {argEvidenceChecks.length > 0 && !editing && (
          <div style={{
            marginTop: 8,
            marginBottom: 6,
            borderTop: '1px solid #f1f5f9',
            paddingTop: 8,
          }}>
            <div style={{
              fontSize: 11,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 6,
              fontFamily: 'var(--font-body)',
            }}>
              Supporting Evidence
            </div>
            {argEvidenceChecks.map(ec => (
              <div key={ec.id} style={{
                marginBottom: 4,
                padding: '5px 8px',
                background: '#f8fafc',
                borderRadius: 5,
                borderLeft: '3px solid #86efac',
              }}>
                <EvidenceItem ec={ec} stopPropagation={true} postId={sourcePostId} />
              </div>
            ))}
          </div>
        )}
        {/* Media thumbnail */}
        {node.media_urls && node.media_urls.length > 0 && !editing && (
          <div style={{ marginBottom: 6 }}>
            {node.media_urls.slice(0, 1).map((url, i) =>
              url && url.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                <img key={i} src={url} alt=""
                  onClick={onViewThread}
                  style={{ height: 60, maxWidth: '100%', objectFit: 'cover', borderRadius: 4, border: '1px solid var(--color-border)', cursor: 'pointer' }} />
              ) : (
                <LinkPreview key={i} meta={null} url={url} />
              )
            )}
          </div>
        )}

        {/* Edit UI */}
        {editing && (
          <div style={{ marginBottom: 8 }} onClick={e => e.stopPropagation()}>
            <textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              style={{
                width: '100%', minHeight: 80,
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 4, color: 'var(--color-text-primary)',
                fontSize: 13, padding: '6px 8px',
                fontFamily: 'var(--font-body)', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
            {editError && <p style={{ color: 'var(--color-invalidated)', fontSize: 12, margin: '4px 0', fontFamily: 'var(--font-body)' }}>{editError}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={handleEditSubmit} disabled={editSubmitting}
                style={{
                  padding: '4px 14px', background: 'var(--color-brand)', color: '#fff',
                  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  fontWeight: 600, fontFamily: 'var(--font-body)',
                }}>
                {editSubmitting ? 'Re-analyzing...' : 'Save & Re-analyze'}
              </button>
              <button onClick={() => { setEditing(false); setEditError(''); }}
                style={{
                  padding: '4px 14px', background: 'transparent',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-muted)', borderRadius: 4,
                  cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font-body)',
                }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Invalidation reason — shown when red with no visible replies */}
        {node.status === 'red' && node.reply_count === 0 && !editing && !node.status_reasoning && (
          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', fontStyle: 'italic', margin: '4px 0 0', fontFamily: 'var(--font-body)' }}>
            ↑ Challenged by related arguments in this debate
          </p>
        )}

        {/* Reply count link — navigate to thread without showing inline synopses */}
        {node.reply_count > 0 && !editing && (
          <button onClick={e => { e.stopPropagation(); onViewThread(); }}
            style={{
              background: 'transparent', border: 'none', color: 'var(--color-brand)',
              fontSize: 11, cursor: 'pointer', padding: '4px 0 0', textAlign: 'left',
              fontFamily: 'var(--font-body)', fontWeight: 500,
            }}>
            View {node.reply_count} {node.reply_count === 1 ? 'reply' : 'replies'} →
          </button>
        )}

        {/* Incident summary card — shown for argument-classified nodes that have incidents */}
        {node.id && !node.is_definition_thread
          && node.argument_type !== 'evidence_challenge'
          && COALESCE_ARG_CLASS(node) === 'argument'
          && !editing && (
          <IncidentSummaryCard
            parentArgId={node.id}
            user={user}
            rawSubmitReply={rawSubmitReply}
            sourcePostId={sourcePostId}
          />
        )}

        {/* Evidence challenges — shown nested at the bottom of each logical argument card */}
        {node.id && !node.is_definition_thread && node.argument_type !== 'evidence_challenge' && !editing && (
          <EvidenceChallengeSection
            parentArgId={node.id}
            user={user}
            rawSubmitReply={rawSubmitReply}
            sourcePostId={sourcePostId}
          />
        )}

        {node.can_receive_replies && user && !editing && (
          <CardReplyBox ref={inlineBoxRef} onSubmit={onSubmitReply} />
        )}
        {!node.can_receive_replies && node.status === 'red' && user && !editing && (
          <CardDefenseBox ref={inlineBoxRef} onSubmit={onSubmitReply} />
        )}
      </div>
      )} {/* end cardExpanded */}
    </div>
  );
}

// ── Claims panel ──────────────────────────────────────────────────────────────
const CLAIM_STATUS_CONFIG = {
  verified:          { color: 'var(--color-verified)', bg: 'var(--color-verified-bg)', border: 'var(--color-verified)', label: '✅ Verified' },
  invalidated:       { color: 'var(--color-invalidated)', bg: 'var(--color-invalidated-bg)', border: 'var(--color-invalidated)', label: '❌ Invalidated' },
  disputed:          { color: 'var(--color-disputed)', bg: 'var(--color-disputed-bg)', border: 'var(--color-disputed)', label: '❓ Disputed' },
  plausible:         { color: '#7c3aed', bg: 'rgba(124,58,237,0.07)', border: '#7c3aed', label: '🔮 Plausible' },
  implausible:       { color: '#374151', bg: 'rgba(55,65,81,0.07)', border: '#374151', label: '🚫 Implausible' },
  contested:         { color: 'var(--color-disputed)', bg: 'var(--color-disputed-bg)', border: 'var(--color-disputed)', label: '⚡ Contested' },
  pending_definition:{ color: '#d97706', bg: 'rgba(217,119,6,0.08)', border: '#d97706', label: '⏳ Awaiting definition' },
  neutral:           { color: 'var(--color-text-muted)', bg: 'transparent', border: 'var(--color-border)', label: '○ Unproven' },
  unverified:        { color: 'var(--color-text-muted)', bg: 'transparent', border: 'var(--color-border)', label: '○ Unproven' },
};
const CLAIM_SORT_ORDER = { invalidated: 0, implausible: 0, pending_definition: 1, disputed: 1, contested: 1, verified: 2, plausible: 2, neutral: 3, unverified: 3 };

function ClaimsPanel({ claims, claimStatuses }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  if (!claims || claims.length === 0) return null;

  const statusByIndex = {};
  if (claimStatuses) {
    for (const cs of claimStatuses) statusByIndex[cs.claim_index] = cs;
  }

  const sortByStatus = (a, b) => {
    const sa = CLAIM_SORT_ORDER[a.statusInfo?.status] ?? 1;
    const sb = CLAIM_SORT_ORDER[b.statusInfo?.status] ?? 1;
    return sa - sb;
  };

  const enriched = claims.map((c, i) => ({
    ...c,
    index: i,
    statusInfo: statusByIndex[c.claim_index ?? i] || null,
  }));
  const primarySorted = enriched.filter(c => c.is_primary).sort(sortByStatus);
  const allSorted = [...enriched].sort(sortByStatus);
  const defaultDisplay = primarySorted.slice(0, 5);
  const displayed = showAll ? allSorted : defaultDisplay;
  const hasMore = defaultDisplay.length < claims.length;

  return (
    <div style={{ marginTop: 16, border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: 'var(--color-surface-hover)', border: 'none',
          cursor: 'pointer', fontSize: 13, fontWeight: 600,
          color: 'var(--color-text-secondary)', textAlign: 'left',
          fontFamily: 'var(--font-body)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          AI-identified claims
          <span title="These are the claims the AI found in this post." style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 16, height: 16, borderRadius: '50%', background: 'var(--color-border)',
            color: 'var(--color-text-muted)', fontSize: 10, fontWeight: 700,
            cursor: 'help', flexShrink: 0,
          }}>i</span>
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div style={{ background: 'var(--color-surface)' }}>
          {displayed.map((c, i) => {
            const sc = CLAIM_STATUS_CONFIG[c.statusInfo?.status];
            return (
              <div
                key={c.index}
                style={{
                  padding: '10px 14px',
                  borderBottom: (i < displayed.length - 1 || hasMore) ? '1px solid var(--color-border)' : 'none',
                  background: sc ? sc.bg : 'var(--color-surface)',
                  borderLeft: sc ? `3px solid ${sc.border}` : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {sc && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: sc.color,
                      background: sc.color + '22', padding: '2px 7px',
                      borderRadius: 10, flexShrink: 0, marginTop: 2,
                      fontFamily: 'var(--font-body)',
                    }}>
                      {sc.label}
                    </span>
                  )}
                  {c.claim_type === 'prediction' && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#7c3aed',
                      background: 'rgba(124,58,237,0.1)', padding: '2px 7px',
                      borderRadius: 10, flexShrink: 0, marginTop: 2,
                      fontFamily: 'var(--font-body)',
                    }}>PREDICTION</span>
                  )}
                  {c.claim_type === 'opinion' && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: 'var(--color-text-muted)',
                      background: 'var(--color-surface-hover)', padding: '2px 7px',
                      borderRadius: 10, flexShrink: 0, marginTop: 2,
                      fontFamily: 'var(--font-body)',
                    }}>OPINION</span>
                  )}
                  <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-text-primary)', margin: 0, lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>{c.claim_text}</p>
                </div>
                {c.statusInfo?.strongest_rebuttal_synopsis && (
                  <p style={{ fontSize: 12, color: 'var(--color-invalidated)', margin: '4px 0 0', fontStyle: 'italic', lineHeight: 1.4, fontFamily: 'var(--font-body)' }}>
                    Counter: {c.statusInfo.strongest_rebuttal_synopsis}
                  </p>
                )}
              </div>
            );
          })}
          {hasMore && (
            <button
              onClick={() => setShowAll(s => !s)}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'var(--color-surface-hover)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                fontFamily: 'var(--font-body)',
                textAlign: 'center',
              }}
            >
              {showAll ? 'Show fewer' : `Show all ${claims.length} claims`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function pill(textColor, bgColor, borderColor) {
  return {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
    color: textColor, background: bgColor, border: `1px solid ${borderColor}`,
    padding: '2px 10px', borderRadius: 20, display: 'inline-block',
    fontFamily: 'var(--font-body)',
  };
}

// ─── UserContributionsPanel ────────────────────────────────────────────────
// Lists the requesting user's contributions on a thread with their post
// content + the node context. Provides Edit and Delete actions that hit the
// existing /reprocess and /delete endpoints.
function UserContributionsPanel({ postId, onChange }) {
  const [open, setOpen] = useState(false);
  const [contribs, setContribs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editText, setEditText] = useState('');
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/posts/${postId}/user-contributions`);
      setContribs(r.data.contributions || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePanel = () => {
    if (!open && contribs === null) load();
    setOpen(o => !o);
  };

  const startEdit = (c) => {
    setEditingPostId(c.post_id);
    setEditText(c.post_content || '');
  };

  const saveEdit = async (c) => {
    try {
      await api.post(`/posts/${c.post_id}/reprocess`, { content: editText });
      setEditingPostId(null);
      setEditText('');
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const deletePost = async (c) => {
    if (!window.confirm('Delete this post? Its contributions will be removed and orphaned nodes will be quarantined.')) return;
    try {
      await api.delete(`/posts/${c.post_id}`);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <button
        onClick={togglePanel}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: 'var(--color-surface-hover)', border: 'none',
          cursor: 'pointer', fontSize: 13, fontWeight: 600,
          color: 'var(--color-text-secondary)', textAlign: 'left',
          fontFamily: 'var(--font-body)',
        }}
      >
        <span>👤 Your contributions on this thread</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div style={{ background: 'var(--color-surface)', padding: 12 }}>
          {loading && <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Loading…</p>}
          {error && <p style={{ fontSize: 12, color: 'var(--color-invalidated)' }}>{error}</p>}
          {!loading && contribs && contribs.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0 }}>You haven't contributed to this thread yet.</p>
          )}
          {!loading && contribs && contribs.length > 0 && contribs.map(c => (
            <div key={c.contribution_id} style={{
              padding: '10px 0', borderBottom: '1px solid var(--color-border)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                <span>{c.node_is_original ? 'root post' : `level ${c.node_depth}`}</span>
                <span> · {c.node_stance} · {c.node_status}</span>
                {c.is_originator ? <span> · originator</span> : <span> · contribution</span>}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                {(c.node_synopsis || '').slice(0, 100)}
              </div>
              {editingPostId === c.post_id ? (
                <>
                  <textarea
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    style={{ width: '100%', minHeight: 80, fontSize: 13, padding: 8, fontFamily: 'var(--font-body)' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button onClick={() => saveEdit(c)} style={{ fontSize: 12, padding: '4px 10px' }}>Save</button>
                    <button onClick={() => { setEditingPostId(null); setEditText(''); }} style={{ fontSize: 12, padding: '4px 10px' }}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', marginBottom: 6 }}>
                    {(c.post_content || c.contribution_text || '').slice(0, 280)}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* Original posts can't be edited (route returns 400) — hide edit button on originator-of-root contributions */}
                    {!(c.node_is_original && c.is_originator) && (
                      <button onClick={() => startEdit(c)} style={{ fontSize: 12, padding: '4px 10px' }}>Edit</button>
                    )}
                    <button onClick={() => deletePost(c)} style={{ fontSize: 12, padding: '4px 10px', color: 'var(--color-invalidated)' }}>Delete</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
